import React, { useState, useEffect, useCallback } from 'react';
import { t } from './tokens';
import { SavedChartState, AppMode, JamSessionState, JamSessionRole, ChartSetList } from './types';
import type { DetectedChord } from './features/midi-daw/types/midi';
import { useModalState, useChartState, useAudioPlayback, useMidiHandlers, useJamSession, useDriveSync, useAutoSave, useResponsiveLayout, useChartCRUD, useSetListCRUD, useStripeHandlers, useGigNavigation, useChordInspector, useMeasureEdit, getDefaultState } from './hooks';
import type { ResponsiveMode } from './hooks';

// Development mode check - set to false for production builds
const IS_DEVELOPMENT = false;

// Secret debug mode - accessible with konami code or special sequence
const ENABLE_SECRET_DEBUG = false;

// Welcome screen demo toggle - set to true to show welcome screen
const SHOW_WELCOME_DEMO = true;

// Global flag to allow navigation during checkout (bypasses beforeunload warning)
// Set this to true before Stripe redirect, false otherwise
(window as any).ALLOW_CHECKOUT_NAVIGATION = false;

import ChartGrid from './components/ChartGrid';
import ChartGridAdvanced from './components/ChartGridAdvanced';
import ChordPalette from './components/ChordPalette';
import LoadChartModal from './components/LoadChartModal';
import CollectionManager from './components/CollectionManager';
import ChordInspector from './components/ChordInspector';
import MidiPanel from './components/MidiPanel';
import JamSessionControls from './components/JamSessionControls';
import DebugOverlay from './components/DebugOverlay';
import InsertMeasuresDialog from './components/InsertMeasuresDialog';
import DeleteMeasureDialog from './components/DeleteMeasureDialog';
import { WebRTCDebugger } from './components/WebRTCDebugger';
import WelcomeScreen from './components/WelcomeScreen';
import LandingPageDesktop from './components/LandingPageDesktop';
import UpgradeModal from './components/UpgradeModal';
import MidiDawModal from './components/MidiDawModal';
import NewChartChoiceModal from './components/NewChartChoiceModal';
import SettingsPanel from './components/SettingsPanel';
import AccountPanel from './components/AccountPanel';
import Header from './components/Header';
import DesktopHeader from './components/DesktopHeader';
import GigHeader from './components/GigHeader';
import GigToolbar from './components/GigToolbar';
import JamHeader from './components/JamHeader';
import JamDesktopBar, { useIsDesktopJam } from './components/JamDesktopBar';
import JamSessionMenuPreview from './components/JamSessionMenuPreview';
import LoadingOverlay from './components/LoadingOverlay';
import JamSessionLoadingModal from './components/JamSessionLoadingModal';
import ContactModal from './components/ContactModal';
import HelpModal from './components/HelpModal';
import PrivacyPolicyModal from './components/PrivacyPolicyModal';
import TermsOfServiceModal from './components/TermsOfServiceModal';
import LegalNoticeModal from './components/LegalNoticeModal';
import PremiumSuccessModal from './components/PremiumSuccessModal';
import { PrintableChart } from './components/PrintableChart';
import FeatureLimitModal from './components/FeatureLimitModal';
import ArchitecturePage from './components/ArchitectureExplorer/ArchitecturePage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SubscriptionProvider, useSubscription } from './contexts/SubscriptionContext';
import webRTCService from './utils/webrtc';

import * as db from './utils/db';
import * as drive from './utils/drive';
import { FreemiumMessageKey } from './utils/freemiumLimits';
import PDFExportModal from './components/PDFExportModal';

// Toggle Switch Component for Right Dock
const ToggleSwitch: React.FC<{ label: string, enabled: boolean, onToggle: () => void, id: string }> = ({ label, enabled, onToggle, id }) => (
    <div className="flex items-center justify-between w-full">
        <label htmlFor={id} className="font-semibold text-sm text-gray-300">{label}</label>
        <label className="relative inline-flex items-center cursor-pointer">
            <input 
                id={id}
                type="checkbox" 
                className="sr-only peer"
                checked={enabled}
                onChange={onToggle}
            />
            <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyan-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-600"></div>
        </label>
    </div>
);



const App: React.FC = () => {
  // Subscription access
  const { hasFeature, upgradeToPremium, subscription, setUpgradeModalTrigger, setDevMode } = useSubscription();
  const { user, signInAnonymously } = useAuth();
  
  // Modal state management (extracted to useModalState hook)
  const modals = useModalState();
  
  // Secret debug mode state
  const [secretDebugEnabled, setSecretDebugEnabled] = useState(ENABLE_SECRET_DEBUG);
  const [keySequence, setKeySequence] = useState<string>('');

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingChart, setIsLoadingChart] = useState(false);
  const [loadingChartMessage, setLoadingChartMessage] = useState('Loading chart...');
  const [isFontLoaded, setIsFontLoaded] = useState(false);
  const [showWelcome, setShowWelcome] = useState(SHOW_WELCOME_DEMO);

  const completeWelcome = () => {
    setShowWelcome(false);
  };

  // Feature limit modal state
  const [featureLimitModalOpen, setFeatureLimitModalOpen] = useState(false);
  const [featureLimitMessageKey, setFeatureLimitMessageKey] = useState<FreemiumMessageKey | undefined>(undefined);

  // Account panel state
  const [isAccountPanelOpen, setIsAccountPanelOpen] = useState(false);

  // Tracks whether the New-chart choice modal (Chord Grid / Piano Rec) was opened
  // from the welcome/landing screen (skip confirm) or from the in-app menu (confirm first).
  const [newChartChoiceFromWelcome, setNewChartChoiceFromWelcome] = useState(false);

  // Mobile accordion state for edit mode top bar
  const [isMobileTitleOpen, setIsMobileTitleOpen] = useState(false);
  const [isMobileRhythmOpen, setIsMobileRhythmOpen] = useState(false);
  const [mobileMeasureInput, setMobileMeasureInput] = useState('');
  const [mobileBpmInput, setMobileBpmInput] = useState('');

  // App mode state (edition or gig) - jam is NOT a mode!
  const [mode, setMode] = useState<AppMode>('edition');

  // Jam Session State - separate from app mode (declared early for useChartState dependency)
  const [jamSessionState, setJamSessionState] = useState<JamSessionState>('idle');
  const [jamSessionRole, setJamSessionRole] = useState<JamSessionRole>(null);
  const [jamSessionCode, setJamSessionCode] = useState<string>('');
  const [isJamMenuOpen, setIsJamMenuOpen] = useState<boolean>(false);

  // Forces the Piano Recorder to open straight into the full-screen score (skips piano roll) — Gig Mode "Voir la partition"
  const [midiDawStartInScoreFullScreen, setMidiDawStartInScoreFullScreen] = useState(false);

  // Determine if we're in read-only mode (ONLY for jam session clients, never for hosts)
  const isReadOnlyMode = (jamSessionState === 'connected' || jamSessionState === 'fallback-server') && jamSessionRole === 'client';
  
  // Determine if we should use gig mode styling (gig mode OR jam session client)
  const isGigModeView = mode === 'gig' || mode === 'sandbox' || isReadOnlyMode;

  // Chart state management (extracted to useChartState hook)
  const {
    songTitle, songAuthor, songStyle, numberOfMeasures, timeSignature, keySignature,
    sourceChart, transposeAmount, audioSettings, uiSettings, activeChord,
    invalidChords, clipboard, displayedChart, repeatPairMap, recordedNotes,
    setSongTitle, setSongAuthor, setSongStyle, setNumberOfMeasures, setTimeSignature,
    setKeySignature, setSourceChart, setTransposeAmount, setAudioSettings, setUiSettings,
    setActiveChord, setRecordedNotes, updateSourceChart, applyLoadedState,
    handleUndo, handleNumMeasuresChange, handleSubdivisionChange, handleChordClick,
    handleChordBuild, handleAnnotateMeasure, handleLyricChange, handleCopyMeasure,
    handlePasteMeasure, handleTimeSignatureChange, handleTranspose,
    handleLyricsSizeChange, handleGigChordScaleChange,
  } = useChartState(mode, isReadOnlyMode, hasFeature, upgradeToPremium);

  // Audio playback management (extracted to useAudioPlayback hook)
  const {
    isPlaying, currentMeasure, currentChordIndex, startMeasureIndex,
    setStartMeasureIndex, setIsPlaying, setCurrentMeasure, setCurrentChordIndex,
    chartHasFine, chartHasToCoda,
    handlePlayPause, handleRewind, handleStop, handlePlayChord,
    handleSetStartMeasure,
  } = useAudioPlayback(displayedChart, repeatPairMap, audioSettings, timeSignature, mode, isReadOnlyMode);

  // MIDI input management (extracted to useMidiHandlers hook)
  const {
    midiInputs, selectedMidiInputId, setSelectedMidiInputId, lastMidiMessage,
  } = useMidiHandlers(activeChord, updateSourceChart, audioSettings);

  // Jam session management (extracted to useJamSession hook)
  const {
    joinSessionCode, setJoinSessionCode, isJoiningSession, isQRCodeLoading,
    connectedPeers, webRTCError, setWebRTCError,
    handleCreateJamSession, handleJoinJamSession, handleLeaveJamSession, handleCloseJamDialog,
  } = useJamSession(
    { jamSessionState, setJamSessionState, jamSessionRole, setJamSessionRole, jamSessionCode, setJamSessionCode },
    {
      songTitle, songAuthor, songStyle, sourceChart, numberOfMeasures,
      timeSignature, keySignature, transposeAmount, audioSettings, uiSettings,
      setSongTitle, setSongAuthor, setSongStyle, setSourceChart,
      setNumberOfMeasures, setTimeSignature, setKeySignature,
      setTransposeAmount, setAudioSettings, setUiSettings,
    },
    user, signInAnonymously, subscription.isPremium, isLoading, setShowWelcome,
    modals.closeJamSessionDialog, setFeatureLimitMessageKey, setFeatureLimitModalOpen,
  );

  const [savedCharts, setSavedCharts] = useState<string[]>([]);
  const [savedChartsData, setSavedChartsData] = useState<SavedChartState[]>([]);
  
  // Set List Management State (formerly Collection Management)
  const [setLists, setSetLists] = useState<ChartSetList[]>([]);
  const [activeSetListId, setActiveSetListId] = useState<string | null>(null);
  
  // Gig Mode Set List Navigation
  const [gigSetListId, setGigSetListId] = useState<string | null>(null);
  const [gigCurrentSongIndex, setGigCurrentSongIndex] = useState<number>(0);
  // const [isGuestSong, setIsGuestSong] = useState(false); // Commented out - not currently used
  
  
  // Responsive layout and device detection (extracted to useResponsiveLayout hook)
  const {
    responsiveMode, setResponsiveMode, viewportHeight,
    measuredLayout, deviceInfo, deviceCategory,
    getResponsiveGridClasses, getHeightBasedGridStyle,
  } = useResponsiveLayout(numberOfMeasures, isGigModeView, mode);


  // Hide static footer from index.html when React loads, and manage visibility based on mode
  useEffect(() => {
    const staticFooter = document.getElementById('static-footer');
    if (staticFooter) {
      staticFooter.style.display = isGigModeView ? 'none' : 'none'; // Always hide - React footer takes over
    }
  }, [isGigModeView]);

  useEffect(() => {
    if (mode !== 'edition' || isReadOnlyMode || (!isMobileTitleOpen && !isMobileRhythmOpen)) {
      return;
    }

    const handleScrollClose = () => {
      setIsMobileTitleOpen(false);
      setIsMobileRhythmOpen(false);
    };

    window.addEventListener('scroll', handleScrollClose, { passive: true });
    return () => window.removeEventListener('scroll', handleScrollClose);
  }, [mode, isReadOnlyMode, isMobileTitleOpen, isMobileRhythmOpen]);

  useEffect(() => {
    setMobileMeasureInput(String(numberOfMeasures));
  }, [numberOfMeasures]);

  useEffect(() => {
    setMobileBpmInput(String(audioSettings.bpm));
  }, [audioSettings.bpm]);

  // User preferences
  const [userPreferences, setUserPreferences] = useState({
    startAppOnLastChart: false, // true = last chart, false = new chart
  });

  // Connect subscription context to upgrade modal
  useEffect(() => {
    console.log('🔗 Setting up modal trigger');
    const modalTrigger = (triggerFeature?: string) => {
      console.log('🎯 Modal trigger called with feature:', triggerFeature);
      modals.openUpgradeModal(triggerFeature);
    };
    
    console.log('🔧 Calling setUpgradeModalTrigger with:', modalTrigger);
    setUpgradeModalTrigger(modalTrigger);
    
    return () => {
      console.log('🧹 Cleaning up modal trigger');
      setUpgradeModalTrigger(null);
    };
  }, []); // Remove dependency to prevent re-runs

  // Secret debug mode activation (type "debug" to enable)
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Ignore keys when typing in input fields
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }
      
      const newSequence = (keySequence + event.key.toLowerCase()).slice(-8); // Increased to 8 for "debugoff"
      setKeySequence(newSequence);
      
      if (newSequence.endsWith('debug') && !secretDebugEnabled) {
        setSecretDebugEnabled(true);
        // Auto-enable debug overlay when secret debug is activated
        setUiSettings(prev => ({ ...prev, isDebugOverlayVisible: true }));
        console.log('🔓 Secret debug mode activated! Debug overlay enabled.');
        // Clear sequence after activation
        setKeySequence('');
      } else if (newSequence.endsWith('debugoff') && secretDebugEnabled) {
        setSecretDebugEnabled(false);
        // Auto-disable debug overlay when debug mode is turned off
        setUiSettings(prev => ({ ...prev, isDebugOverlayVisible: false }));
        modals.closeWebRTCDebugger(); // Also hide WebRTC debugger
        console.log('🔒 Secret debug mode deactivated! Debug overlay disabled.');
        // Clear sequence after deactivation
        setKeySequence('');
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [keySequence, secretDebugEnabled, setUiSettings]);

  // Wrap hook's applyLoadedState with playback reset
  const applyLoadedStateWithPlaybackReset = useCallback((parsed: SavedChartState) => {
    applyLoadedState(parsed);
    setStartMeasureIndex(0);
    handleStop();
  }, [applyLoadedState, handleStop, setStartMeasureIndex]);

    const loadSavedChartTitles = useCallback(async () => {
    try {
        const chartTitles = await db.getAllChartTitles();
        setSavedCharts(chartTitles);
        
        // Also load full chart data for filtering
        const chartsData = await db.getAllCharts();
        setSavedChartsData(chartsData);
    } catch (error) {
        console.error("Failed to load chart titles from DB:", error);
    }
  }, []);

  // Cloud sync / Google Drive management (extracted to useDriveSync hook)
  const {
    userProfile, isGapiReady, setSyncStatus,
    showDriveReconnectPrompt, setShowDriveReconnectPrompt,
    requestDrivePermissions, disconnectDrive, fullSync,
  } = useDriveSync(user, hasFeature, setSetLists, loadSavedChartTitles);

  // Auto-save and change tracking (extracted to useAutoSave hook)
  const {
    hasUnsavedChanges,
    saveStatus, setSaveStatus, saveStatusTimerRef, markSaved,
  } = useAutoSave(
    {
      songTitle, songAuthor, songStyle, numberOfMeasures,
      timeSignature, keySignature, sourceChart,
      transposeAmount, audioSettings, uiSettings, recordedNotes,
    },
    userProfile,
    () => {
      if (jamSessionState !== 'idle') {
        webRTCService.leaveSession();
      }
    },
  );

  const loadSetLists = useCallback(async () => {
    try {
        const savedSetLists = await db.getSetLists();
        setSetLists(savedSetLists);

        // If authenticated, also try to load from cloud and merge
        if (isGapiReady && userProfile) {
          try {
            const cloudSetLists = await drive.loadCollectionsFromDrive();
            if (cloudSetLists.length > 0) {
              // Simple merge: cloud data takes precedence for now
              // In future, could implement proper conflict resolution
              setSetLists(cloudSetLists);
              await db.saveSetLists(cloudSetLists);
            }
          } catch (error) {
            console.error('Failed to load set lists from cloud:', error);
          }
        }
    } catch (error) {
        console.error("Failed to load set lists from DB:", error);
        
        // One-time fix: if it's a schema error, clear the database and reload
        if (error instanceof Error && error.message.includes('object stores was not found')) {
          console.log('Database schema issue detected, clearing database...');
          try {
            await db.clearDatabase();
            // After clearing, try to reload set lists (will be empty but schema will be correct)
            const savedSetLists = await db.getSetLists();
            setSetLists(savedSetLists);
          } catch (clearError) {
            console.error('Failed to clear database:', clearError);
          }
        }
    }
  }, [isGapiReady, userProfile]);

  // Chart CRUD operations (extracted to useChartCRUD hook)
  const {
    handleSave, handleSaveAs, handleEmptyChart, confirmEmptyChart,
    handleLoadChart, handleDeleteChart, handleRefreshCharts,
    handleExportPDF, handlePDFExportWithOptions,
    handleExportJSON, handleImportJSON,
  } = useChartCRUD({
    chartFields: {
      songTitle, songAuthor, songStyle, numberOfMeasures,
      timeSignature, keySignature, sourceChart,
      transposeAmount, audioSettings, uiSettings, recordedNotes,
    },
    setSongTitle, setSongAuthor, setSongStyle,
    updateSourceChart,
    userProfile, isGapiReady, setSyncStatus, fullSync,
    loadSavedChartTitles, markSaved, setSaveStatus, saveStatusTimerRef,
    applyLoadedStateWithPlaybackReset, savedChartsData,
    subscription, hasFeature, upgradeToPremium,
    setFeatureLimitMessageKey, setFeatureLimitModalOpen,
    jamSessionState, jamSessionRole,
    setLoadingChartMessage, setIsLoadingChart,
    modals: {
      openNameRequiredModal: modals.openNameRequiredModal,
      closeSaveAsModal: modals.closeSaveAsModal,
      setSaveAsTitle: modals.setSaveAsTitle,
      openEmptyChartModal: modals.openEmptyChartModal,
      closeEmptyChartModal: modals.closeEmptyChartModal,
      openLoadModal: modals.openLoadModal,
      closeLoadModal: modals.closeLoadModal,
      setIsLoadModalRefreshing: modals.setIsLoadModalRefreshing,
      openPDFExportModal: modals.openPDFExportModal,
    },
  });

  // Set List CRUD operations (extracted to useSetListCRUD hook)
  const {
    handleCreateSetList, handleUpdateSetList, handleDeleteSetList,
    handleAddChartToSetList, handleRemoveChartFromSetList,
  } = useSetListCRUD({
    setLists, setSetLists, activeSetListId, setActiveSetListId,
    isGapiReady, userProfile,
  });

  // Stripe checkout verification + portal (extracted to useStripeHandlers hook)
  const { handleOpenStripePortal } = useStripeHandlers({
    user, subscription, setDevMode,
    modals: { openPremiumSuccessModal: modals.openPremiumSuccessModal },
  });

  // Chord inspector state and handlers (extracted to useChordInspector hook)
  const {
    inspectorData, setInspectorData, setIsInspectorHovered,
    isInspectorPinned, setIsInspectorPinned, handleChordHover, handleChordLeave,
    cancelAutoClose, cancelCloseTimeout,
  } = useChordInspector({ mode, uiSettings });

  // Measure insert/delete handlers (extracted to useMeasureEdit hook)
  const {
    handleInsertMeasures, handleInsertMeasuresConfirm, handleInsertMeasuresCancel,
    handleDeleteMeasure, handleDeleteMeasureConfirm, handleDeleteMeasureCancel,
  } = useMeasureEdit({
    updateSourceChart, setNumberOfMeasures,
    currentMeasure, setCurrentMeasure, setCurrentChordIndex, setIsPlaying,
    startMeasureIndex, setStartMeasureIndex,
    modals: {
      openInsertMeasuresDialog: modals.openInsertMeasuresDialog,
      closeInsertMeasuresDialog: modals.closeInsertMeasuresDialog,
      openDeleteMeasureDialog: modals.openDeleteMeasureDialog,
      closeDeleteMeasureDialog: modals.closeDeleteMeasureDialog,
    },
  });

  // Gig mode navigation (extracted to useGigNavigation hook)
  const {
    selectGigSetList, clearGigSetList,
    handleOpenChartFromGig, handleOpenSetlistFromGig, handleNavigateSongFromGig,
  } = useGigNavigation({
    setLists, gigSetListId, setGigSetListId, gigCurrentSongIndex, setGigCurrentSongIndex,
    jamSessionState, jamSessionRole, uiSettings,
    userProfile, isGapiReady, subscription,
    handleLoadChart, fullSync, loadSavedChartTitles, loadSetLists,
    setLoadingChartMessage, setIsLoadingChart,
    setFeatureLimitMessageKey, setFeatureLimitModalOpen,
    modals: {
      closeGigSetListSelector: modals.closeGigSetListSelector,
      openLoadModal: modals.openLoadModal,
      openGigSetListSelector: modals.openGigSetListSelector,
    },
  });

  // Wait for Archivo Black font to load before showing menu
  useEffect(() => {
    const loadFont = async () => {
      try {
        await document.fonts.load('400 20px "Archivo Black"');
        setIsFontLoaded(true);
      } catch (error) {
        console.warn('Font loading failed, proceeding anyway:', error);
        setIsFontLoaded(true); // Proceed even if font load fails
      }
    };
    loadFont();
  }, []);

  // Load initial chart from IndexedDB
  useEffect(() => {
    const loadData = async () => {
      try {
        let chartToLoad: SavedChartState | undefined;
        
        // Only load last chart if user preference allows it
        if (userPreferences.startAppOnLastChart) {
          const lastOpenedTitle = await db.getLastOpenedChartTitle();

          if (lastOpenedTitle) {
            chartToLoad = await db.getChart(lastOpenedTitle);
          }
          
          if (!chartToLoad) {
              const allTitles = await db.getAllChartTitles();
              if(allTitles.length > 0) {
                  chartToLoad = await db.getChart(allTitles[0]);
              }
          }
        }
        // If userPreferences.startAppOnLastChart is false, chartToLoad stays undefined

        if (chartToLoad) {
          applyLoadedStateWithPlaybackReset(chartToLoad);
        } else {
            const defaultState = getDefaultState();
            applyLoadedStateWithPlaybackReset(defaultState);
            await db.saveChart(defaultState);
            await db.setLastOpenedChartTitle(defaultState.songTitle);
        }

        // Load set lists and chart titles
        await loadSetLists();
        await loadSavedChartTitles();
      } catch (error) {
        console.error("Failed to load initial data from IndexedDB:", error);
        applyLoadedStateWithPlaybackReset(getDefaultState());
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [applyLoadedStateWithPlaybackReset, userPreferences.startAppOnLastChart]);


  // Handle openSettings URL parameter from landing page
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const openSettings = urlParams.get('openSettings');
    
    if (openSettings === 'true' && !isLoading) {
      console.log('📋 Opening Settings panel from landing page');
      // Clear the URL parameter
      window.history.replaceState({}, document.title, window.location.pathname);
      // Open the settings panel
      modals.openSettings();
    }
  }, [isLoading, modals]);

  // When switching to gig mode or joining a jam session, close the chord palette
  useEffect(() => {
    if (isGigModeView) {
      setActiveChord(null);
    }
  }, [isGigModeView]);

  // Wrappers for chart handlers that also touch playback state
  const handleNumMeasuresChangeLocal = (newSize: number) => {
    handleNumMeasuresChange(newSize, mode, startMeasureIndex, setStartMeasureIndex);
  };

  const commitMobileMeasureInput = () => {
    const digitsOnly = mobileMeasureInput.replace(/\D+/g, '');
    if (!digitsOnly) {
      setMobileMeasureInput(String(numberOfMeasures));
      return;
    }

    handleNumMeasuresChangeLocal(Math.max(1, parseInt(digitsOnly, 10)));
  };

  const commitMobileBpmInput = () => {
    const digitsOnly = mobileBpmInput.replace(/\D+/g, '');
    if (!digitsOnly) {
      setMobileBpmInput(String(audioSettings.bpm));
      return;
    }

    const nextBpm = Math.min(400, Math.max(20, parseInt(digitsOnly, 10)));
    setAudioSettings({ ...audioSettings, bpm: nextBpm });
  };





  const appContainerClasses = mode === 'edition' && !isReadOnlyMode
    ? "min-h-screen text-gray-100 flex flex-col px-4 pt-0 pb-4 sm:px-6 sm:pt-0 sm:pb-6 md:px-8 md:pt-0 md:pb-8 font-sans"
    : "min-h-screen text-gray-100 flex flex-col font-sans p-1";

  const appContainerStyle = { backgroundColor: t.bg };

  // Leaving the landing is driven by the session itself: cancelling the jam dialog keeps the landing
  useEffect(() => {
    if (showWelcome && jamSessionState !== 'idle') {
      completeWelcome();
    }
  }, [showWelcome, jamSessionState]);

  // JAM mini-banner shown at fixed top:0 in edit mode (mobile only)
  const jamEditBannerActive = mode === 'edition' && !isReadOnlyMode
    && jamSessionRole === 'host'
    && (jamSessionState === 'hosting' || jamSessionState === 'connected' || jamSessionState === 'fallback-server');

  // JamHeader grows by 14px (host) / 6px (client) when it renders the Figma desktop bar
  const isDesktopJam = useIsDesktopJam();
  const jamHostGigHeader = mode === 'gig' && jamSessionState !== 'idle' && jamSessionRole === 'host';
  const headerFixedHeight = isReadOnlyMode
    ? (isDesktopJam ? '95px' : '89px')
    : mode === 'gig'
      ? (jamHostGigHeader && isDesktopJam ? '145px' : '131px')
      : undefined;

  const activeMeasure = activeChord ? sourceChart[activeChord.measureIndex] : null;

  // Show QR code loading modal (highest priority - should show above everything)
  if (isQRCodeLoading) {
    return <JamSessionLoadingModal isVisible={true} />;
  }

  if (isLoading || !isFontLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#121212' }}>
        <div className="text-cyan-400 font-mono text-xl animate-pulse">Loading Your Charts...</div>
      </div>
    );
  }

  return (
    <div className={appContainerClasses} style={appContainerStyle}>
      {/* Desktop JAM top header — edit mode HOST (Figma #586:256) */}
      {jamEditBannerActive && !modals.showSetListManager && !showWelcome && (
        <div className="hidden lg:block w-full shrink-0" style={{ position: 'relative', zIndex: 130 }}>
          <JamDesktopBar
            isHost
            sessionCode={jamSessionCode}
            musicianCount={connectedPeers.length}
            isJamMenuOpen={isJamMenuOpen}
            onToggleJamMenu={() => setIsJamMenuOpen(prev => !prev)}
            jamMenuContent={
              <JamSessionControls
                sessionState={jamSessionState}
                sessionRole={jamSessionRole}
                sessionCode={jamSessionCode}
                connectedClients={connectedPeers.map(peer => peer.name)}
                onStateChange={setJamSessionState}
                onRoleChange={setJamSessionRole}
                onCodeChange={setJamSessionCode}
                onLeaveSession={handleLeaveJamSession}
                forceExpanded={true}
                onClose={() => setIsJamMenuOpen(false)}
              />
            }
          />
        </div>
      )}
      <header data-debug-id="Level-1:Header" className={`w-full shrink-0 relative transition-all duration-300 ${mode === 'edition' && !isReadOnlyMode ? 'lg:sticky lg:top-0 lg:z-[120]' : ''} ${
        mode === 'gig' || isReadOnlyMode 
          ? (() => {
              // Match grid's actual max-width (grid classes can override wrapper's max-w-7xl)
              if (responsiveMode === 'container') return 'max-w-4xl mx-auto mb-1 py-0';
              if (responsiveMode === 'dynamic') return 'max-w-6xl mx-auto mb-1 py-0';
              if (responsiveMode === 'adaptive') return 'max-w-6xl mx-auto mb-1 py-0';
              return 'max-w-7xl mx-auto mb-1 py-0'; // current/smart/height
            })()
          : 'max-w-7xl lg:max-w-none mx-auto lg:mx-0 mb-1 lg:mb-0 py-1 lg:py-0'  // Edition mode — compact on mobile, full-width on desktop
      }`} style={{ height: headerFixedHeight, display: (modals.showSetListManager || showWelcome) ? 'none' : undefined }}>
        {/* Edition Mode Button - Top Right - only for non-gig modes and when not already in edition mode */}
        {mode !== 'gig' && mode !== 'edition' && jamSessionState !== 'idle' && (
          <button
            onClick={() => setMode('edition')}
            className="absolute top-0 right-0 px-3 py-1.5 sm:px-3 sm:py-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 text-white font-semibold transition-all shadow-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm sm:rounded-lg rounded-full w-8 h-8 sm:w-auto sm:h-auto flex items-center justify-center sm:block z-10"
            title="Edition Mode"
          >
            <span className="sm:hidden text-lg">✕</span>
            <span className="hidden sm:inline">Edition Mode</span>
          </button>
        )}
        
        {/* App Branding */}
        <div className={`${mode === 'gig' || isReadOnlyMode ? 'mb-0' : mode === 'edition' ? 'mb-1' : 'mb-3'}`}>
          {/* Edition mode: Show the new MainMenu component */}
          {mode === 'edition' && !isReadOnlyMode && (
            <>
              {/* Desktop: Figma pixel-perfect header (lg only) */}
              <div className="hidden lg:block">
                <DesktopHeader
                  songTitle={songTitle}
                  onTitleChange={setSongTitle}
                  songAuthor={songAuthor}
                  onAuthorChange={setSongAuthor}
                  songStyle={songStyle}
                  onStyleChange={setSongStyle}
                  isPlaying={isPlaying}
                  onPlayPause={handlePlayPause}
                  onStop={handleStop}
                  onRewind={handleRewind}
                  transposeAmount={transposeAmount}
                  onTranspose={handleTranspose}
                  onNewChart={handleEmptyChart}
                  onOpenChart={async () => {
                    setLoadingChartMessage('Loading charts...');
                    setIsLoadingChart(true);
                    try {
                      if (userProfile && isGapiReady) {
                        await fullSync();
                      }
                      await loadSavedChartTitles();
                      modals.openLoadModal();
                    } finally {
                      setIsLoadingChart(false);
                    }
                  }}
                  onSetlists={async () => {
                    await loadSetLists();
                    await loadSavedChartTitles();
                    modals.openSetListManager();
                  }}
                  onSave={handleSave}
                  onSaveAs={() => {
                    modals.setSaveAsTitle(songTitle);
                    modals.openSaveAsModal();
                  }}
                  onExportPDF={handleExportPDF}
                  onHome={() => setShowWelcome(true)}
                  onPianoRec={() => modals.openMidiDaw()}
                  onJamSession={() => modals.openJamSessionDialog()}
                  onGigMode={() => setMode('gig')}
                  onHelp={() => modals.openHelp()}
                  onAccount={() => {
                    if (isAccountPanelOpen) {
                      setIsAccountPanelOpen(false);
                    } else {
                      setIsAccountPanelOpen(true);
                      modals.closeSettings();
                    }
                  }}
                  onSettings={() => {
                    if (modals.isRightDockOpen) {
                      modals.closeSettings();
                    } else {
                      modals.openSettings();
                      setIsAccountPanelOpen(false);
                    }
                  }}
                  numberOfMeasures={numberOfMeasures}
                  onNumMeasuresChange={handleNumMeasuresChangeLocal}
                  timeSignature={timeSignature}
                  onTimeSignatureChange={handleTimeSignatureChange}
                  keySignature={keySignature}
                  onKeySignatureChange={setKeySignature}
                  audioSettings={audioSettings}
                  onAudioSettingsChange={setAudioSettings}
                />
              </div>
              {/* Mobile: existing Header (desktop portion hidden via className="hidden" in Header.tsx) */}
              <Header
                onOpenChart={async () => {
                  setLoadingChartMessage('Loading charts...');
                  setIsLoadingChart(true);
                  try {
                    if (userProfile && isGapiReady) {
                      await fullSync();
                    }
                    await loadSavedChartTitles();
                    modals.openLoadModal();
                  } finally {
                    setIsLoadingChart(false);
                  }
                }}
                onSetlists={async () => {
                  await loadSetLists();
                  await loadSavedChartTitles();
                  modals.openSetListManager();
                }}
                onNewChart={handleEmptyChart}
                onOpenNewChartChoice={() => {
                  setNewChartChoiceFromWelcome(false);
                  modals.openNewChartChoiceModal();
                }}
                onPianoRec={() => modals.openMidiDaw()}
                onSave={handleSave}
                onSaveAs={() => {
                  modals.setSaveAsTitle(songTitle);
                  modals.openSaveAsModal();
                }}
                onExportPDF={handleExportPDF}
                onHome={() => setShowWelcome(true)}
                onJamSession={() => modals.openJamSessionDialog()}
                onGigMode={() => setMode('gig')}
                onHelp={() => modals.openHelp()}
                onAccount={() => {
                  if (isAccountPanelOpen) {
                    setIsAccountPanelOpen(false);
                  } else {
                    setIsAccountPanelOpen(true);
                    modals.closeSettings();
                  }
                }}
                onSettings={() => {
                  if (modals.isRightDockOpen) {
                    modals.closeSettings();
                  } else {
                    modals.openSettings();
                    setIsAccountPanelOpen(false);
                  }
                }}
                isAccountOpen={isAccountPanelOpen}
                isSettingsOpen={modals.isRightDockOpen}
                saveStatus={saveStatus}
                hasUnsavedChanges={hasUnsavedChanges}
              />
            </>
          )}

          {/* Mode Status Display */}
          <div className={`${mode === 'gig' ? 'mt-1' : 'mt-4'}`}>
            {mode === 'gig' ? (
              <>
                {/* Check if HOST is in Jam Session - use JamHeader instead of GigHeader */}
                {jamSessionState !== 'idle' && jamSessionRole === 'host' ? (
                  <JamHeader
                    collectionName={setLists.find(sl => sl.id === gigSetListId)?.name || ''}
                    currentSongTitle={songTitle}
                    canNavigatePrev={gigCurrentSongIndex > 0}
                    canNavigateNext={gigCurrentSongIndex < (setLists.find(sl => sl.id === gigSetListId)?.chartTitles.length || 0) - 1}
                    onNavigatePrev={() => handleNavigateSongFromGig('prev')}
                    onNavigateNext={() => handleNavigateSongFromGig('next')}
                    onOpenChart={handleOpenChartFromGig}
                    onOpenSetlist={handleOpenSetlistFromGig}
                    isHost={true}
                    sessionCode={jamSessionCode}
                    musicianCount={connectedPeers.length}
                    isJamMenuOpen={isJamMenuOpen}
                    onToggleJamMenu={() => setIsJamMenuOpen(prev => !prev)}
                    showEditionModeButton={true}
                    onEditionMode={() => setMode('edition')}
                    onSettings={() => { modals.openSettings(); setIsAccountPanelOpen(false); }}
                    jamMenuContent={
                      <JamSessionControls
                        key="jam-session-controls"
                        sessionState={jamSessionState}
                        sessionRole={jamSessionRole}
                        sessionCode={jamSessionCode}
                        connectedClients={connectedPeers.map(peer => peer.name)}
                        onStateChange={setJamSessionState}
                        onRoleChange={setJamSessionRole}
                        onCodeChange={setJamSessionCode}
                        onLeaveSession={handleLeaveJamSession}
                        forceExpanded={true}
                        onClose={() => setIsJamMenuOpen(false)}
                      />
                    }
                  />
                ) : (
                  <GigHeader
                    collectionName={setLists.find(sl => sl.id === gigSetListId)?.name || ''}
                    currentSongTitle={songTitle}
                    canNavigatePrev={gigCurrentSongIndex > 0 && !(jamSessionState !== 'idle' && jamSessionRole === 'client')}
                    canNavigateNext={gigCurrentSongIndex < (setLists.find(sl => sl.id === gigSetListId)?.chartTitles.length || 0) - 1 && !(jamSessionState !== 'idle' && jamSessionRole === 'client')}
                    onNavigatePrev={() => handleNavigateSongFromGig('prev')}
                    onNavigateNext={() => handleNavigateSongFromGig('next')}
                    onOpenChart={handleOpenChartFromGig}
                    onOpenSetlist={handleOpenSetlistFromGig}
                    showEditionModeButton={jamSessionState === 'idle' || jamSessionRole === 'host'}
                    onEditionMode={() => setMode('edition')}
                    onSettings={() => { modals.openSettings(); setIsAccountPanelOpen(false); }}
                    hasScore={recordedNotes.length > 0}
                    onViewScore={() => { setMidiDawStartInScoreFullScreen(true); modals.openMidiDaw(); }}
                  />
                )}
              </>
            ) : isReadOnlyMode ? (
              // Jam Session Client: Show JamHeader (same UI as GigMode but without Open Chart/Setlist)
              <>
                <JamHeader
                  collectionName={setLists.find(sl => sl.id === gigSetListId)?.name || ''}
                  currentSongTitle={songTitle}
                  canNavigatePrev={false}
                  canNavigateNext={false}
                  onNavigatePrev={() => {}}
                  onNavigateNext={() => {}}
                  isHost={false}
                  showEditionModeButton={true}
                  onEditionMode={handleLeaveJamSession}
                  isJamMenuOpen={isJamMenuOpen}
                  onToggleJamMenu={() => setIsJamMenuOpen(prev => !prev)}
                  jamMenuContent={
                    <div style={{ backgroundColor: '#1A1311', borderTop: '1px solid rgba(255,255,255,0.1)', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <button
                        onClick={() => { setIsJamMenuOpen(false); handleLeaveJamSession(); }}
                        style={{ fontFamily: 'Kumbh Sans, sans-serif', fontWeight: 600, fontSize: '13px', letterSpacing: '0.05em', textTransform: 'uppercase', color: '#D3CCBD', backgroundColor: '#3D3028', border: 'none', borderRadius: '22px', padding: '12px 20px', cursor: 'pointer', width: '100%' }}
                      >
                        Back to Edit Mode
                      </button>
                      <button
                        onClick={() => { setIsJamMenuOpen(false); handleLeaveJamSession(); setShowWelcome(true); }}
                        style={{ fontFamily: 'Kumbh Sans, sans-serif', fontWeight: 600, fontSize: '13px', letterSpacing: '0.05em', textTransform: 'uppercase', color: '#FFF', backgroundColor: '#E4393F', border: 'none', borderRadius: '22px', padding: '12px 20px', cursor: 'pointer', width: '100%' }}
                      >
                        Back to Landing Page
                      </button>
                    </div>
                  }
                />
              </>
            ) : mode === 'sandbox' ? (
              <span className="text-lg font-bold text-yellow-400 bg-yellow-400/10 px-3 py-1 rounded-lg border border-yellow-400/30">
                🧪 Sandbox Mode
              </span>
            ) : null}
          </div>
        </div>
      </header>

      {/* Backdrop — closes jam menu on outside click (host and client) */}
      {isJamMenuOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setIsJamMenuOpen(false)} />
      )}

      {/* JAM mini-banner FIXED top:0 — edit mode HOST, mobile only — design identique à JamHeader GIG mode */}
      {jamEditBannerActive && (
        <div className="lg:hidden" style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200 }}>
          <div style={{ height: '26px', backgroundColor: '#E4393F', overflow: 'hidden', position: 'relative' }}>
            {/* JAM group #443:355 — x:20, y:5 */}
            <div style={{ position: 'absolute', left: '4.975%', top: '5px', width: '48px', height: '17px' }}>
              <img src="/figma-assets/icons/jam-header/jam-icon.svg" width="15" height="17" alt="" style={{ display: 'block' }} />
              <span style={{ position: 'absolute', left: '25px', top: '2px', fontFamily: 'Kumbh Sans, sans-serif', fontWeight: 500, fontSize: '10px', color: '#FFFFFF', whiteSpace: 'nowrap' }}>JAM</span>
            </div>
            {/* HOSTING group #443:354 — x:83, y:7 */}
            <div style={{ position: 'absolute', left: '20.647%', top: '7px', width: '58px', height: '15px' }}>
              <span style={{ position: 'absolute', left: 0, top: 0, fontFamily: 'Kumbh Sans, sans-serif', fontWeight: 500, fontSize: '10px', color: '#FFFFFF', whiteSpace: 'nowrap' }}>HOSTING</span>
              <span style={{ position: 'absolute', left: '50px', top: '2px', display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#34C759' }} />
            </div>
            {/* CODE #439:210 — x:156, y:7 */}
            <div style={{ position: 'absolute', left: '38.806%', top: '7px', fontFamily: 'Kumbh Sans, sans-serif', fontSize: '10px', color: '#FFFFFF', whiteSpace: 'nowrap' }}>
              <span style={{ fontWeight: 500 }}>CODE : </span>
              <span style={{ fontWeight: 700, color: '#FFC2C4' }}>{jamSessionCode || '------'}</span>
            </div>
            {/* MUSICIANS #439:211 — x:259, y:7 */}
            <div style={{ position: 'absolute', left: '64.428%', top: '7px', fontFamily: 'Kumbh Sans, sans-serif', fontSize: '10px', color: '#FFFFFF', whiteSpace: 'nowrap' }}>
              <span style={{ fontWeight: 500 }}>MUSICIANS : </span>
              <span style={{ fontWeight: 700, color: '#FFE5E6' }}>{connectedPeers.length}</span>
            </div>
            {/* keyboard_arrow_up #439:182 — x:355, y:0 */}
            <button
              onClick={() => setIsJamMenuOpen(prev => !prev)}
              style={{ position: 'absolute', left: '88.308%', top: 0, background: 'none', border: 'none', padding: 0, cursor: 'pointer', transform: isJamMenuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
            >
              <img src="/figma-assets/icons/jam-header/keyboard-arrow-up.svg" width="26" height="26" alt="Toggle JAM menu" style={{ display: 'block' }} />
            </button>
          </div>
          {isJamMenuOpen && (
            <div style={{ position: 'absolute', top: '26px', left: 0, right: 0, zIndex: 100, maxHeight: 'calc(100vh - 60px)', overflowY: 'auto', animation: 'jam-menu-open 0.2s ease' }}>
              <JamSessionControls
                sessionState={jamSessionState}
                sessionRole={jamSessionRole}
                sessionCode={jamSessionCode}
                connectedClients={connectedPeers.map(peer => peer.name)}
                onStateChange={setJamSessionState}
                onRoleChange={setJamSessionRole}
                onCodeChange={setJamSessionCode}
                onLeaveSession={handleLeaveJamSession}
                forceExpanded={true}
                onClose={() => setIsJamMenuOpen(false)}
              />
            </div>
          )}
        </div>
      )}

      {/* Drive Reconnect Prompt - for Premium users who had Cloud Sync enabled on another device */}
      {showDriveReconnectPrompt && mode === 'edition' && (
        <div className="w-full max-w-7xl mx-auto mb-3 px-4">
          <div className="bg-gradient-to-r from-cyan-900/50 to-blue-900/50 border border-cyan-500/50 rounded-lg p-3 flex items-center justify-between gap-4 shadow-lg">
            <div className="flex items-center gap-3">
              <span className="text-2xl">☁️</span>
              <div>
                <p className="text-cyan-200 font-semibold text-sm">Reconnect to Google Drive</p>
                <p className="text-cyan-300/70 text-xs">Your charts are synced in the cloud. Connect to access them on this device.</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={requestDrivePermissions}
                className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold text-sm rounded-lg transition-colors shadow-md"
              >
                Connect
              </button>
              <button
                onClick={() => setShowDriveReconnectPrompt(false)}
                className="p-2 text-cyan-300/50 hover:text-cyan-200 transition-colors"
                title="Dismiss"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WebRTC Error Display */}
      {webRTCError && (
        <div className="w-full max-w-lg mx-auto bg-red-900/50 border border-red-500 p-4 rounded-lg mb-6 shadow-lg">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 24 24">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="text-red-200 font-semibold">Connection Error</span>
          </div>
          <p className="text-red-300 mt-2">{webRTCError}</p>
          <button 
            onClick={() => setWebRTCError(null)}
            className="mt-3 px-3 py-1 bg-red-700 hover:bg-red-600 text-white rounded text-sm transition-colors"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Client Rhythm Info - Hidden for jam session clients to keep UI clean */}

      {/* ── Mobile Edit Mode Top Bar (lg:hidden) ── */}
      {mode === 'edition' && !isReadOnlyMode && (
        <div className="lg:hidden w-full px-3 pt-0 pb-2" style={{ backgroundColor: t.bg }}>

          {/* Title Accordion — expands as overlay over chord grid (#303:208) */}
          <div className="relative mb-2">
            <button
              onClick={() => { const next = !isMobileTitleOpen; setIsMobileTitleOpen(next); if (next) setIsMobileRhythmOpen(false); }}
              className="w-full relative flex items-center justify-center"
              style={{
                minHeight: '44px', border: 'none', cursor: 'pointer',
                borderRadius: isMobileTitleOpen ? '23px 23px 0 0' : '23px',
                backgroundColor: isMobileTitleOpen ? t.cream : t.pill,
                boxShadow: isMobileTitleOpen ? '0px 6px 9.7px rgba(0,0,0,0.11)' : 'none',
                transition: 'border-radius 0.28s cubic-bezier(0.22, 1, 0.36, 1), background-color 0.28s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.28s cubic-bezier(0.22, 1, 0.36, 1)',
              }}
            >
              <span style={{ fontFamily: 'Kumbh Sans, sans-serif', fontSize: '24px', textTransform: 'uppercase', letterSpacing: '0.05em', color: t.darkBrown, fontWeight: 400 }}>
                {songTitle || 'Title'}
              </span>
              <img src="/figma-assets/icons/keyboard-arrow-up.svg" width="24" height="24" alt=""
                style={{ position: 'absolute', right: '16px', top: '50%', transform: isMobileTitleOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)', transition: 'transform 0.2s ease' }} />
            </button>
            <div
              className="absolute left-0 right-0"
              aria-hidden={!isMobileTitleOpen}
              style={{
                top: '44px',
                backgroundColor: t.pill,
                borderRadius: '0 0 23px 23px',
                zIndex: 50,
                overflow: 'hidden',
                maxHeight: isMobileTitleOpen ? '260px' : '0px',
                opacity: isMobileTitleOpen ? 1 : 0,
                transform: isMobileTitleOpen ? 'translateY(0)' : 'translateY(-8px)',
                pointerEvents: isMobileTitleOpen ? 'auto' : 'none',
                transition: 'max-height 0.3s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.22s ease-out, transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
              }}
            >
              <div className="flex flex-col gap-3 px-4 pb-5 pt-3"
                style={{ opacity: isMobileTitleOpen ? 1 : 0, transition: 'opacity 0.2s ease-out' }}
              >
                <div className="flex items-center gap-3">
                  <span style={{ fontFamily: 'Kumbh Sans, sans-serif', fontVariantCaps: 'small-caps', fontSize: '18px', color: t.darkBrown, textAlign: 'right', minWidth: '80px', flexShrink: 0 }}>Title :</span>
                  <div style={{ flex: 1, borderRadius: '15px', backgroundColor: t.cream, padding: '6px 12px' }}>
                    <input type="text" value={songTitle} onChange={e => setSongTitle(e.target.value)}
                      className="w-full bg-transparent border-none outline-none"
                      style={{ fontFamily: 'Kumbh Sans, sans-serif', fontVariantCaps: 'small-caps', fontSize: '18px', color: t.amber }}
                      placeholder="Song name" />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span style={{ fontFamily: 'Kumbh Sans, sans-serif', fontVariantCaps: 'small-caps', fontSize: '18px', color: t.darkBrown, textAlign: 'right', minWidth: '80px', flexShrink: 0 }}>Author :</span>
                  <div style={{ flex: 1, borderRadius: '15px', backgroundColor: t.cream, padding: '6px 12px' }}>
                    <input type="text" value={songAuthor} onChange={e => setSongAuthor(e.target.value)}
                      className="w-full bg-transparent border-none outline-none"
                      style={{ fontFamily: 'Kumbh Sans, sans-serif', fontVariantCaps: 'small-caps', fontSize: '18px', color: t.amber }}
                      placeholder="Author name" />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span style={{ fontFamily: 'Kumbh Sans, sans-serif', fontVariantCaps: 'small-caps', fontSize: '18px', color: t.darkBrown, textAlign: 'right', minWidth: '80px', flexShrink: 0 }}>Style :</span>
                  <div style={{ flex: 1, borderRadius: '15px', backgroundColor: t.cream, padding: '6px 12px' }}>
                    <select value={songStyle} onChange={e => setSongStyle(e.target.value)}
                      className="w-full bg-transparent border-none outline-none"
                      style={{ fontFamily: 'Kumbh Sans, sans-serif', fontVariantCaps: 'small-caps', fontSize: '18px', color: t.amber }}
                    >
                      <option value="Jazz">Jazz</option>
                      <option value="Swing">Swing</option>
                      <option value="Latin">Latin</option>
                      <option value="Ballad">Ballad</option>
                      <option value="Soul">Soul</option>
                      <option value="Pop">Pop</option>
                      <option value="Rock">Rock</option>
                      <option value="Bossa">Bossa</option>
                      <option value="Country">Country</option>
                      <option value="Reggae">Reggae</option>
                    </select>
                  </div>
                </div>
                {/* Plus circle — future: expand to year/collaboration metadata (#303:215) */}
                <div className="flex justify-end">
                  <button disabled aria-label="More metadata" style={{ background: 'none', border: 'none', cursor: 'default', opacity: 0.4 }}>
                    <img src="/figma-assets/icons/plus-circle.svg" width="32" height="32" alt="" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Rhythm & Sound Accordion — expands as overlay (#303:208) */}
          <div className="relative mb-2">
            <button
              onClick={() => { const next = !isMobileRhythmOpen; setIsMobileRhythmOpen(next); if (next) setIsMobileTitleOpen(false); }}
              className="w-full relative flex items-center justify-center"
              style={{
                minHeight: '44px', border: 'none', cursor: 'pointer',
                borderRadius: isMobileRhythmOpen ? '23px 23px 0 0' : '23px',
                backgroundColor: isMobileRhythmOpen ? t.cream : t.pill,
                boxShadow: isMobileRhythmOpen ? '0px 6px 9.7px rgba(0,0,0,0.11)' : 'none',
                transition: 'border-radius 0.28s cubic-bezier(0.22, 1, 0.36, 1), background-color 0.28s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.28s cubic-bezier(0.22, 1, 0.36, 1)',
              }}
            >
              <span style={{ fontFamily: 'Kumbh Sans, sans-serif', fontSize: '24px', textTransform: 'uppercase', letterSpacing: '0.05em', color: t.darkBrown, fontWeight: 400 }}>
                Rhythm &amp; Sound
              </span>
              <img src="/figma-assets/icons/keyboard-arrow-up.svg" width="24" height="24" alt=""
                style={{ position: 'absolute', right: '16px', top: '50%', transform: isMobileRhythmOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)', transition: 'transform 0.2s ease' }} />
            </button>
            <div
              aria-hidden={!isMobileRhythmOpen}
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: '44px',
                backgroundColor: '#D9D9D9',
                borderRadius: '0 0 23px 23px',
                zIndex: 50,
                maxHeight: isMobileRhythmOpen ? 'calc(100vh - 140px)' : '0px',
                overflowY: isMobileRhythmOpen ? 'auto' : 'hidden',
                opacity: isMobileRhythmOpen ? 1 : 0,
                transform: isMobileRhythmOpen ? 'translateY(0)' : 'translateY(-8px)',
                pointerEvents: isMobileRhythmOpen ? 'auto' : 'none',
                padding: isMobileRhythmOpen ? '12px 10px 14px' : '0 10px',
                display: 'flex',
                flexDirection: 'column',
                gap: isMobileRhythmOpen ? 10 : 0,
                transition: 'max-height 0.32s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.22s ease-out, transform 0.32s cubic-bezier(0.22, 1, 0.36, 1), padding 0.26s cubic-bezier(0.22, 1, 0.36, 1), gap 0.26s cubic-bezier(0.22, 1, 0.36, 1)',
              }}
            >
                <div style={{ backgroundColor: 'rgba(157, 219, 23, 0.46)', borderRadius: 13, border: '2px solid #B7B7BB', padding: '10px 10px 12px' }}>
                  <div style={{ fontFamily: 'Kumbh Sans, sans-serif', fontWeight: 900, fontSize: '16px', letterSpacing: '0.42em', textTransform: 'uppercase', color: '#89A947', textAlign: 'center', marginBottom: 8 }}>sound</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 6 }}>
                      <span style={{ fontFamily: 'Kumbh Sans, sans-serif', fontSize: 'clamp(16px, 4.8vw, 24px)', color: '#271B13' }}>Key</span>
                      <div style={{ position: 'relative', minHeight: 40, backgroundColor: '#F4F1EB', borderRadius: 15 }}>
                        <select value={keySignature} onChange={e => setKeySignature(e.target.value as typeof keySignature)}
                          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', background: 'transparent', border: 'none', outline: 'none', paddingLeft: 12, paddingRight: 40, fontFamily: 'Kumbh Sans, sans-serif', fontSize: 'clamp(16px, 4.8vw, 24px)', color: '#DB9817', appearance: 'none', cursor: 'pointer' }}
                        >
                          {(['C','G','D','A','E','B','F#','C#','F','Bb','Eb','Ab','Db','Gb','Cb'] as const).map(k => (
                            <option key={k} value={k}>{k}</option>
                          ))}
                        </select>
                        <img src="/figma-assets/mobile-accordion/pill-arrow.svg" style={{ position: 'absolute', right: 8, top: '50%', width: 20, height: 20, transform: 'translateY(-50%)', pointerEvents: 'none' }} alt="" />
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 6 }}>
                      <span style={{ fontFamily: 'Kumbh Sans, sans-serif', fontSize: 'clamp(16px, 4.8vw, 24px)', color: '#271B13' }}>Timbre:</span>
                      <div style={{ position: 'relative', minHeight: 40, backgroundColor: '#F4F1EB', borderRadius: 15 }}>
                        <select value={audioSettings.timbre} onChange={e => setAudioSettings({ ...audioSettings, timbre: e.target.value as typeof audioSettings.timbre })}
                          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', background: 'transparent', border: 'none', outline: 'none', paddingLeft: 12, paddingRight: 40, fontFamily: 'Kumbh Sans, sans-serif', fontSize: 'clamp(16px, 4.8vw, 24px)', color: '#DB9817', appearance: 'none', cursor: 'pointer' }}
                        >
                          <option value="Piano">Piano</option>
                          <option value="Sine">Sine Wave</option>
                          <option value="Synth">Synth</option>
                        </select>
                        <img src="/figma-assets/mobile-accordion/pill-arrow.svg" style={{ position: 'absolute', right: 8, top: '50%', width: 20, height: 20, transform: 'translateY(-50%)', pointerEvents: 'none' }} alt="" />
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 6 }}>
                      <span style={{ fontFamily: 'Kumbh Sans, sans-serif', fontSize: 'clamp(16px, 4.8vw, 24px)', color: '#271B13' }}>Voicing:</span>
                      <div style={{ position: 'relative', minHeight: 40, backgroundColor: '#F4F1EB', borderRadius: 15 }}>
                        <select value={audioSettings.voicing} onChange={e => setAudioSettings({ ...audioSettings, voicing: e.target.value as typeof audioSettings.voicing })}
                          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', background: 'transparent', border: 'none', outline: 'none', paddingLeft: 12, paddingRight: 40, fontFamily: 'Kumbh Sans, sans-serif', fontSize: 'clamp(16px, 4.8vw, 24px)', color: '#DB9817', appearance: 'none', cursor: 'pointer' }}
                        >
                          <option value="Spread">Spread</option>
                          <option value="Stacked">Stacked</option>
                        </select>
                        <img src="/figma-assets/mobile-accordion/pill-arrow.svg" style={{ position: 'absolute', right: 8, top: '50%', width: 20, height: 20, transform: 'translateY(-50%)', pointerEvents: 'none' }} alt="" />
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 6 }}>
                      <span style={{ fontFamily: 'Kumbh Sans, sans-serif', fontSize: 'clamp(16px, 4.8vw, 24px)', color: '#271B13' }}>Dynamics:</span>
                      <div style={{ position: 'relative', minHeight: 40, backgroundColor: '#F4F1EB', borderRadius: 15 }}>
                        <select value={audioSettings.dynamics} onChange={e => setAudioSettings({ ...audioSettings, dynamics: e.target.value as typeof audioSettings.dynamics })}
                          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', background: 'transparent', border: 'none', outline: 'none', paddingLeft: 12, paddingRight: 40, fontFamily: 'Kumbh Sans, sans-serif', fontSize: 'clamp(16px, 4.8vw, 24px)', color: '#DB9817', appearance: 'none', cursor: 'pointer' }}
                        >
                          <option value="Block">Block</option>
                          <option value="Arpeggiated">Arpeggiated</option>
                        </select>
                        <img src="/figma-assets/mobile-accordion/pill-arrow.svg" style={{ position: 'absolute', right: 8, top: '50%', width: 20, height: 20, transform: 'translateY(-50%)', pointerEvents: 'none' }} alt="" />
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ backgroundColor: 'rgba(83, 210, 212, 0.71)', borderRadius: 13, border: '2px solid #B7B7BB', padding: '10px 10px 12px' }}>
                  <div style={{ fontFamily: 'Kumbh Sans, sans-serif', fontWeight: 900, fontSize: '16px', letterSpacing: '0.42em', textTransform: 'uppercase', color: '#35A8A8', textAlign: 'center', marginBottom: 8 }}>rythm</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 6 }}>
                      <span style={{ fontFamily: 'Kumbh Sans, sans-serif', fontSize: 'clamp(16px, 4.8vw, 24px)', color: '#271B13' }}>Measure</span>
                      <div style={{ minHeight: 40, backgroundColor: '#F4F1EB', borderRadius: 15, padding: '0 12px', display: 'flex', alignItems: 'center' }}>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={mobileMeasureInput}
                          onFocus={e => e.currentTarget.select()}
                          onChange={e => setMobileMeasureInput(e.target.value.replace(/\D+/g, ''))}
                          onBlur={commitMobileMeasureInput}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              commitMobileMeasureInput();
                              e.currentTarget.blur();
                            }
                          }}
                          style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', fontFamily: 'Kumbh Sans, sans-serif', fontSize: 'clamp(16px, 4.8vw, 24px)', color: '#DB9817' }}
                        />
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 6 }}>
                      <span style={{ fontFamily: 'Kumbh Sans, sans-serif', fontSize: 'clamp(16px, 4.8vw, 24px)', color: '#271B13' }}>Time sig</span>
                      <div style={{ position: 'relative', minHeight: 40, backgroundColor: '#F4F1EB', borderRadius: 15 }}>
                        <select value={timeSignature.beats} onChange={e => handleTimeSignatureChange(parseInt(e.target.value, 10))}
                          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', background: 'transparent', border: 'none', outline: 'none', paddingLeft: 12, paddingRight: 40, fontFamily: 'Kumbh Sans, sans-serif', fontSize: 'clamp(16px, 4.8vw, 24px)', color: '#DB9817', appearance: 'none', cursor: 'pointer' }}
                        >
                          <option value="2">2/4</option>
                          <option value="3">3/4</option>
                          <option value="4">4/4</option>
                          <option value="5">5/4</option>
                          <option value="6">6/8</option>
                        </select>
                        <img src="/figma-assets/mobile-accordion/pill-arrow.svg" style={{ position: 'absolute', right: 8, top: '50%', width: 20, height: 20, transform: 'translateY(-50%)', pointerEvents: 'none' }} alt="" />
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 6 }}>
                      <span style={{ fontFamily: 'Kumbh Sans, sans-serif', fontSize: 'clamp(16px, 4.8vw, 24px)', color: '#271B13' }}>BPM</span>
                      <div style={{ minHeight: 40, backgroundColor: '#F4F1EB', borderRadius: 15, padding: '0 12px', display: 'flex', alignItems: 'center' }}>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={mobileBpmInput}
                          onFocus={e => e.currentTarget.select()}
                          onChange={e => setMobileBpmInput(e.target.value.replace(/\D+/g, ''))}
                          onBlur={commitMobileBpmInput}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              commitMobileBpmInput();
                              e.currentTarget.blur();
                            }
                          }}
                          style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', fontFamily: 'Kumbh Sans, sans-serif', fontSize: 'clamp(16px, 4.8vw, 24px)', color: '#DB9817' }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
        </div>
      )}

      {mode === 'edition' && !isReadOnlyMode && (
        <div className="lg:hidden w-full px-3 pb-2 sticky z-20" style={{ backgroundColor: t.bg, top: jamEditBannerActive ? '26px' : '0px' }}>
          {/* Transport Bar — layoutMode:none → enfants absolus (#302:206, bg=#302:205) */}
          <div style={{ position: 'relative', height: '86px', width: '100%' }}>
            {/* Background #302:205 — 380×86, fills #362820, borderRadius 23px */}
            <div style={{ position: 'absolute', inset: 0, backgroundColor: t.transport, borderRadius: '23px' }} />

            {/* Rewind #290:166 — x:30/380=7.9%, y:0, 85×85 */}
            <button onClick={handleRewind} aria-label="Rewind"
              style={{ position: 'absolute', left: '7.9%', top: 0, width: '85px', height: '85px', background: 'none', border: 'none', outline: 'none', cursor: 'pointer', padding: 0 }}
            >
              <img src="/figma-assets/icons/rewind.svg" width="85" height="85" alt="" style={{ display: 'block' }} />
            </button>

            {/* Play/Stop #290:151 — x:150/380=39.5%, y:2, 81×81 */}
            <button onClick={isPlaying ? handleStop : handlePlayPause} aria-label={isPlaying ? 'Stop' : 'Play'}
              style={{ position: 'absolute', left: '39.5%', top: '2px', width: '81px', height: '81px', background: 'none', border: 'none', outline: 'none', cursor: 'pointer', padding: 0 }}
            >
              <img src={isPlaying ? '/figma-assets/icons/stop-circle.svg' : '/figma-assets/icons/play-circle.svg'} width="81" height="81" alt="" style={{ display: 'block' }} />
            </button>

            {/* Transpose — centré à x:309/380=81.3% (#290:168 label, #291:184 value) */}
            <div style={{ position: 'absolute', left: '81.3%', top: 0, bottom: 0, transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px' }}>
              <span style={{ fontFamily: 'Kumbh Sans, sans-serif', fontSize: '8.5px', letterSpacing: '0.42em', textTransform: 'uppercase', color: t.beige, opacity: 0.53, whiteSpace: 'nowrap' }}>Transpose</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button onClick={() => handleTranspose(-1)} style={{ background: 'none', border: 'none', outline: 'none', appearance: 'none', cursor: 'pointer', padding: 0 }}>
                  <img src="/figma-assets/icons/transport/minus-square.svg" width="34" height="34" alt="−" style={{ display: 'block' }} />
                </button>
                <span style={{ fontFamily: 'Kumbh Sans, sans-serif', fontSize: '17px', textTransform: 'uppercase', color: t.beige, minWidth: '20px', textAlign: 'center' }}>{transposeAmount}</span>
                <button onClick={() => handleTranspose(1)} style={{ background: 'none', border: 'none', outline: 'none', appearance: 'none', cursor: 'pointer', padding: 0 }}>
                  <img src="/figma-assets/icons/transport/plus-square.svg" width="34" height="34" alt="+" style={{ display: 'block' }} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <main 
        data-debug-id="Level-1:MainContent"
        className={`w-full clickable-area flex-grow ${isGigModeView ? '' : 'max-w-7xl mx-auto'}`} 
        style={{ display: modals.showSetListManager ? 'none' : undefined }}
        onClick={e => e.stopPropagation()}
      >
        {mode === 'sandbox' ? (
          // Sandbox Mode - Special testing environment
          <div className="w-full max-w-7xl mx-auto">
            {/* Sandbox Header with Real-time Measurements */}
            <div className="bg-yellow-900/20 border border-yellow-400/30 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-bold text-yellow-400">🧪 Height-Responsive Sandbox</h2>
                  <div className="text-sm text-yellow-300/80">
                    Real-time geometry testing environment
                  </div>
                  <div className="px-3 py-1 bg-green-500/20 border border-green-400/50 rounded text-green-300 text-sm font-bold">
                    SANDBOX MODE ACTIVE
                  </div>
                </div>
                <div className="flex gap-4 text-sm">
                  <div className="text-yellow-300">
                    <span className="text-yellow-400 font-mono">Viewport:</span> {deviceInfo.viewport.width}×{deviceInfo.viewport.height}px
                  </div>
                  <div className="text-yellow-300">
                    <span className="text-yellow-400 font-mono">Available:</span> {viewportHeight - 123}px height
                  </div>
                  <div className="text-yellow-300">
                    <span className="text-yellow-400 font-mono">Per Measure:</span> {((viewportHeight - 123) / Math.ceil(displayedChart.length / 4)).toFixed(1)}px
                  </div>
                  <div className="text-yellow-300">
                    <span className="text-yellow-400 font-mono">Orientation:</span> {deviceInfo.isPortrait ? '📱 Portrait' : '🖥️ Landscape'}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Visual Debugging Tools */}
            <div className="bg-gray-800/50 border border-yellow-400/20 rounded-lg p-4 mb-4">
              <h3 className="text-lg font-bold text-yellow-400 mb-3">🔬 Visual Debugging Tools</h3>
              
              {/* Device Detection Panel */}
              <div className="mb-4 bg-indigo-900/20 border border-indigo-400/30 rounded-lg p-3">
                <h4 className="text-indigo-400 font-mono text-sm mb-3">
                  📱 DEVICE DETECTION - {deviceCategory.icon} {deviceCategory.category.toUpperCase()} DETECTED
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                  <div className="space-y-1">
                    <div className="text-indigo-300 font-semibold">Viewport</div>
                    <div className="text-white">{deviceInfo.viewport.width} × {deviceInfo.viewport.height}px</div>
                    <div className="text-gray-400">Aspect: {deviceInfo.aspectRatio}</div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-indigo-300 font-semibold">Screen Resolution</div>
                    <div className="text-white">{deviceInfo.screen.width} × {deviceInfo.screen.height}px</div>
                    <div className="text-gray-400">DPR: {deviceInfo.devicePixelRatio}x</div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-indigo-300 font-semibold">Orientation</div>
                    <div className="text-white">{deviceInfo.isPortrait ? '📱 Portrait' : '🖥️ Landscape'}</div>
                    <div className="text-gray-400">{deviceInfo.orientation}° ({deviceInfo.orientationType})</div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-indigo-300 font-semibold">Adaptive Settings</div>
                    <div className="text-white">{deviceCategory.optimalColumns} columns optimal</div>
                    <div className="text-gray-400">{deviceCategory.measureSize} measures, {deviceCategory.features} features</div>
                  </div>
                </div>
                
                {/* Adaptive Recommendations */}
                <div className="mt-3 p-2 bg-blue-900/20 border border-blue-400/30 rounded">
                  <div className="text-blue-300 text-xs font-bold">🤖 ADAPTIVE RECOMMENDATIONS:</div>
                  <div className="text-blue-200 text-xs mt-1">
                    {deviceCategory.category === 'mobile' && deviceInfo.isPortrait && 
                      "📱 Mobile Portrait: Single column layout for maximum readability. Large touch targets recommended."
                    }
                    {deviceCategory.category === 'mobile' && !deviceInfo.isPortrait && 
                      "📱 Mobile Landscape: Two column layout for performance reading. Simplified UI."
                    }
                    {deviceCategory.category === 'tablet' && deviceInfo.isPortrait && 
                      "📋 Tablet Portrait: Two column layout ideal for sheet music reading. Balanced features."
                    }
                    {deviceCategory.category === 'tablet' && !deviceInfo.isPortrait && 
                      "📋 Tablet Landscape: Four column layout for performance. Full chord visibility."
                    }
                    {deviceCategory.category === 'desktop' && 
                      `🖥️ Desktop: ${deviceCategory.optimalColumns} column layout optimized for ${deviceInfo.viewport.width >= 2560 ? '4K+ resolution' : deviceInfo.viewport.width >= 1920 ? 'high resolution' : 'standard resolution'}. Full feature set available.`
                    }
                  </div>
                </div>
              </div>
              
              {/* Real-time Height Calculation Display */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="bg-blue-900/20 border border-blue-400/30 rounded p-3">
                  <div className="text-blue-400 font-mono text-sm mb-1">CALCULATION</div>
                  <div className="text-white">
                    <div>Total Height: <span className="text-blue-300 font-mono">{window.innerHeight}px</span></div>
                    <div>Header: <span className="text-red-300 font-mono">-123px</span></div>
                    <div>Available: <span className="text-green-300 font-mono">{window.innerHeight - 123}px</span></div>
                    <div className="border-t border-gray-600 mt-1 pt-1">
                      <div>÷ 4 rows = <span className="text-yellow-300 font-mono">{((window.innerHeight - 123) / 4).toFixed(1)}px</span> per measure</div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-purple-900/20 border border-purple-400/30 rounded p-3">
                  <div className="text-purple-400 font-mono text-sm mb-1">CONSTRAINTS</div>
                  <div className="text-white">
                    <div>Min Size: <span className="text-red-300 font-mono">80px</span></div>
                    <div>Current: <span className="text-green-300 font-mono">{((window.innerHeight - 123) / 4).toFixed(0)}px</span></div>
                    <div>Status: <span className={((window.innerHeight - 123) / 4) >= 80 ? "text-green-300" : "text-red-300"}>
                      {((window.innerHeight - 123) / 4) >= 80 ? "✅ OK" : "❌ TOO SMALL"}
                    </span></div>
                    {((window.innerHeight - 123) / 4) < 80 && (
                      <div className="text-orange-300 text-xs mt-1">Scroll will be enabled</div>
                    )}
                  </div>
                </div>
                
                <div className="bg-green-900/20 border border-green-400/30 rounded p-3">
                  <div className="text-green-400 font-mono text-sm mb-1">RESPONSIVE MODE</div>
                  <div className="text-white">
                    <div>Current: <span className="text-yellow-300 font-mono">{responsiveMode}</span></div>
                    <div>Measures: <span className="text-blue-300 font-mono">{displayedChart.length}</span></div>
                    <div>Columns: <span className="text-green-300 font-mono">4 (fixed)</span></div>
                    <div>Rows: <span className="text-purple-300 font-mono">{Math.ceil(displayedChart.length / 4)}</span></div>
                  </div>
                </div>
              </div>
              
              {/* Height Ruler Visualization */}
              <div className="relative bg-gray-900/50 border border-gray-600 rounded-lg p-4">
                <div className="text-yellow-400 font-mono text-sm mb-2">HEIGHT RULER (Visual Guide)</div>
                <div className="relative h-32 bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-600 rounded">
                  {/* Height markers */}
                  <div className="absolute inset-0 flex flex-col">
                    {/* Total height marker */}
                    <div className="absolute top-0 left-0 right-0 h-px bg-white opacity-50"></div>
                    <div className="absolute top-0 left-1 text-white text-xs">0px (top)</div>
                    
                    {/* Header section */}
                    <div style={{height: `${(123 / window.innerHeight) * 100}%`}} className="bg-red-500/20 border-b border-red-400/50 relative">
                      <div className="absolute bottom-1 left-1 text-red-300 text-xs">Header (123px)</div>
                    </div>
                    
                    {/* Available space */}
                    <div className="flex-1 bg-green-500/10 border-b border-green-400/30 relative">
                      <div className="absolute top-1 left-1 text-green-300 text-xs">Available ({window.innerHeight - 123}px)</div>
                      
                      {/* Row divisions */}
                      {[1, 2, 3].map(row => (
                        <div 
                          key={row}
                          style={{top: `${(row / 4) * 100}%`}} 
                          className="absolute left-0 right-0 h-px bg-yellow-400/50"
                        >
                          <div className="absolute left-1 text-yellow-300 text-xs">
                            Row {row}: {(((window.innerHeight - 123) / 4) * row).toFixed(0)}px
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Bottom marker */}
                    <div className="absolute bottom-0 left-0 right-0 h-px bg-white opacity-50"></div>
                    <div className="absolute bottom-0 left-1 text-white text-xs">{window.innerHeight}px (bottom)</div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Regular ChartGrid for baseline comparison */}
            <ChartGrid
              mode="sandbox"
              chart={displayedChart}
              activeChord={activeChord}
              onChordClick={handleChordClick}
              onSubdivisionChange={handleSubdivisionChange}
              onPlayChord={handlePlayChord}
              timeSignature={timeSignature}
              currentMeasure={currentMeasure}
              currentChordIndex={currentChordIndex}
              startMeasureIndex={startMeasureIndex}
              onSetStartMeasure={handleSetStartMeasure}
              invalidChords={invalidChords}
              clipboard={clipboard}
              onCopyMeasure={handleCopyMeasure}
              onPasteMeasure={handlePasteMeasure}
              onChordHover={handleChordHover}
              onChordLeave={handleChordLeave}
              onLyricChange={handleLyricChange}
              onInsertMeasures={handleInsertMeasures}
              onDeleteMeasure={handleDeleteMeasure}
              lyricsFontSize={uiSettings.lyricsFontSize}
              customGridClasses={getResponsiveGridClasses('gig', numberOfMeasures)}
              customGridStyle={getHeightBasedGridStyle()}
              inspectorMode={uiSettings.inspectorMode}
              inspectorEnabled={uiSettings.inspectorEnabled}
            />
            
            {/* Real-time Measurement Analysis */}
            <div className="mt-6 bg-gray-800/30 border border-yellow-400/20 rounded-lg p-4">
              <h3 className="text-lg font-bold text-yellow-400 mb-3">📊 Live Measurement Analysis</h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Current Layout Analysis */}
                <div className="bg-indigo-900/20 border border-indigo-400/30 rounded p-3">
                  <div className="text-indigo-400 font-mono text-sm mb-2">CURRENT LAYOUT</div>
                  {measuredLayout ? (
                    <div className="space-y-1 text-sm">
                      <div>Columns: <span className="text-green-300 font-mono">{measuredLayout.columns}</span></div>
                      <div>Rows: <span className="text-blue-300 font-mono">{measuredLayout.rows}</span></div>
                      <div>Measures: <span className="text-purple-300 font-mono">{displayedChart.length}</span></div>
                      <div>Empty Slots: <span className="text-gray-300 font-mono">{(measuredLayout.columns * measuredLayout.rows) - displayedChart.length}</span></div>
                      {measuredLayout.customStyle && (
                        <div className="mt-2 pt-2 border-t border-gray-600">
                          <div className="text-yellow-300 text-xs">Custom Grid Style Active:</div>
                          <div className="text-xs text-gray-300 font-mono">
                            {JSON.stringify(measuredLayout.customStyle, null, 1).slice(0, 100)}...
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-gray-400 text-sm">No measured layout available. Switch to Height mode.</div>
                  )}
                </div>
                
                {/* Problem Detection */}
                <div className="bg-orange-900/20 border border-orange-400/30 rounded p-3">
                  <div className="text-orange-400 font-mono text-sm mb-2">PROBLEM DETECTION</div>
                  <div className="space-y-2 text-sm">
                    {/* Height constraint check */}
                    <div className="flex items-center gap-2">
                      {((window.innerHeight - 123) / Math.ceil(displayedChart.length / 4)) >= 80 ? (
                        <span className="text-green-400">✅</span>
                      ) : (
                        <span className="text-red-400">❌</span>
                      )}
                      <span>Height constraint (80px min)</span>
                    </div>
                    
                    {/* Responsive mode check */}
                    <div className="flex items-center gap-2">
                      {responsiveMode === 'height' ? (
                        <span className="text-green-400">✅</span>
                      ) : (
                        <span className="text-yellow-400">⚠️</span>
                      )}
                      <span>Height mode active</span>
                    </div>
                    
                    {/* Measure count check */}
                    <div className="flex items-center gap-2">
                      {displayedChart.length % 4 === 0 ? (
                        <span className="text-green-400">✅</span>
                      ) : (
                        <span className="text-blue-400">ℹ️</span>
                      )}
                      <span>Measure count ({displayedChart.length} {displayedChart.length % 4 === 0 ? 'perfect' : 'will round up'})</span>
                    </div>
                    
                    {/* Current issue analysis */}
                    <div className="mt-3 p-2 bg-gray-800/50 rounded">
                      <div className="text-yellow-300 text-xs font-bold">YOUR ISSUE:</div>
                      <div className="text-gray-300 text-xs mt-1">
                        {((window.innerHeight - 123) / Math.ceil(displayedChart.length / 4)) >= 208 ? 
                          "✅ Should show ~208px measures without scroll" :
                          `❌ Calculated ${((window.innerHeight - 123) / Math.ceil(displayedChart.length / 4)).toFixed(0)}px < expected 208px`
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Interactive Testing Controls */}
              <div className="mt-4 bg-gray-900/50 border border-gray-600 rounded p-3">
                <div className="text-gray-300 font-mono text-sm mb-3">🎯 INTERACTIVE TESTING CONTROLS</div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => {
                      const currentHeight = window.innerHeight;
                      const targetHeight = 957; // Your test case
                      console.log(`🧪 SANDBOX TEST: Simulating ${targetHeight}px height (currently ${currentHeight}px)`);
                      console.log(`📐 Expected: ${targetHeight - 123}px ÷ 4 = ${(targetHeight - 123) / 4}px per measure`);
                      console.log(`📐 Current: ${currentHeight - 123}px ÷ 4 = ${(currentHeight - 123) / 4}px per measure`);
                      console.log(`📱 Device: ${deviceInfo.viewport.width}×${deviceInfo.viewport.height} (${deviceInfo.isPortrait ? 'Portrait' : 'Landscape'})`);
                      alert(`🧪 Test Results:\nExpected: ${(targetHeight - 123) / 4}px per measure\nCurrent: ${((currentHeight - 123) / 4).toFixed(1)}px per measure\nDevice: ${deviceInfo.isPortrait ? 'Portrait' : 'Landscape'} ${deviceInfo.touchSupport ? 'Touch' : 'Desktop'}\n\nCheck console for details!`);
                    }}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-500 border border-blue-400 rounded text-white font-semibold text-sm transition-colors"
                  >
                    🧪 Test 957px Height Scenario
                  </button>
                  <button
                    onClick={() => {
                      console.log(`🔍 CURRENT MEASUREMENTS:`);
                      console.log(`- Viewport: ${deviceInfo.viewport.width}×${deviceInfo.viewport.height}px`);
                      console.log(`- Screen: ${deviceInfo.screen.width}×${deviceInfo.screen.height}px`);
                      console.log(`- Pixel Ratio: ${deviceInfo.devicePixelRatio}x`);
                      console.log(`- Orientation: ${deviceInfo.orientationType} (${deviceInfo.orientation}°)`);
                      console.log(`- Touch Support: ${deviceInfo.touchSupport}`);
                      console.log(`- Available: ${window.innerHeight - 123}px height`);
                      console.log(`- Per measure: ${((window.innerHeight - 123) / Math.ceil(displayedChart.length / 4)).toFixed(1)}px`);
                      console.log(`- Responsive mode: ${responsiveMode}`);
                      console.log(`- Measures: ${displayedChart.length} (${Math.ceil(displayedChart.length / 4)} rows)`);
                      if (measuredLayout) {
                        console.log(`- Layout: ${measuredLayout.columns}×${measuredLayout.rows}`);
                      }
                      alert(`📊 Device measurements logged to console!\nViewport: ${deviceInfo.viewport.width}×${deviceInfo.viewport.height}px\nScreen: ${deviceInfo.screen.width}×${deviceInfo.screen.height}px\nPer measure: ${((window.innerHeight - 123) / Math.ceil(displayedChart.length / 4)).toFixed(1)}px`);
                    }}
                    className="px-4 py-2 bg-green-600 hover:bg-green-500 border border-green-400 rounded text-white font-semibold text-sm transition-colors"
                  >
                    📊 Log Device & Measurements
                  </button>
                  <button
                    onClick={() => {
                      const deviceType = deviceInfo.touchSupport ? 'Mobile/Tablet' : 'Desktop';
                      const recommendation = deviceInfo.isPortrait ? 
                        'Portrait: Try 2-column layout for readability' :
                        'Landscape: 4-column layout optimal for wide screens';
                      console.log(`💡 DEVICE RECOMMENDATIONS:`);
                      console.log(`- Device Type: ${deviceType}`);
                      console.log(`- Current Layout: ${deviceInfo.isPortrait ? 'Portrait' : 'Landscape'}`);
                      console.log(`- Recommendation: ${recommendation}`);
                      console.log(`- DPI Scaling: ${deviceInfo.devicePixelRatio}x (affects text clarity)`);
                      alert(`💡 Layout Recommendations:\n${recommendation}\nDevice: ${deviceType}\nDPI: ${deviceInfo.devicePixelRatio}x scaling`);
                    }}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-500 border border-purple-400 rounded text-white font-semibold text-sm transition-colors"
                  >
                    💡 Get Device Recommendations
                  </button>
                </div>
                <div className="mt-2 text-gray-400 text-xs">
                  💡 Tip: Resize window to see real-time updates above. Open browser console (F12) to see detailed logs.
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Regular Gig/Edition Mode
          <div className={isGigModeView ? 'w-full max-w-7xl mx-auto' : 'w-full'} style={isGigModeView ? { paddingBottom: '82px' } : undefined}>
            {/* Smart Grid Selection: Use advanced grid for Adaptive AI in gig mode */}
            {responsiveMode === 'adaptive' && isGigModeView ? (
              <ChartGridAdvanced
                mode={isGigModeView ? 'gig' : mode}
                chart={displayedChart}
                activeChord={activeChord}
                onChordClick={handleChordClick}
                onSubdivisionChange={handleSubdivisionChange}
                onPlayChord={handlePlayChord}
                timeSignature={timeSignature}
                currentMeasure={currentMeasure}
                currentChordIndex={currentChordIndex}
                startMeasureIndex={startMeasureIndex}
                onSetStartMeasure={handleSetStartMeasure}
                invalidChords={invalidChords}
                clipboard={clipboard}
                onCopyMeasure={handleCopyMeasure}
                onPasteMeasure={handlePasteMeasure}
                onChordHover={handleChordHover}
                onChordLeave={handleChordLeave}
                onLyricChange={handleLyricChange}
                lyricsFontSize={uiSettings.lyricsFontSize}
                gigChordScale={uiSettings.gigChordScale ?? 1.0}
                inspectorMode={uiSettings.inspectorMode}
                inspectorEnabled={uiSettings.inspectorEnabled}
                chartHasFine={chartHasFine}
                chartHasToCoda={chartHasToCoda}
              />
            ) : (
              <ChartGrid
                mode={isGigModeView ? 'gig' : mode}
                chart={displayedChart}
                activeChord={activeChord}
                onChordClick={handleChordClick}
                onSubdivisionChange={handleSubdivisionChange}
                onPlayChord={handlePlayChord}
                timeSignature={timeSignature}
                currentMeasure={currentMeasure}
                currentChordIndex={currentChordIndex}
                startMeasureIndex={startMeasureIndex}
                onSetStartMeasure={handleSetStartMeasure}
                invalidChords={invalidChords}
                clipboard={clipboard}
                onCopyMeasure={handleCopyMeasure}
                onPasteMeasure={handlePasteMeasure}
                onChordHover={handleChordHover}
                onChordLeave={handleChordLeave}
                onLyricChange={handleLyricChange}
                onInsertMeasures={handleInsertMeasures}
                onDeleteMeasure={handleDeleteMeasure}
                lyricsFontSize={uiSettings.lyricsFontSize}
                customGridClasses={getResponsiveGridClasses(isGigModeView ? 'gig' : mode, numberOfMeasures)}
                customGridStyle={getHeightBasedGridStyle()}
                inspectorMode={uiSettings.inspectorMode}
                inspectorEnabled={uiSettings.inspectorEnabled}
                chartHasFine={chartHasFine}
                chartHasToCoda={chartHasToCoda}
              />
            )}
          </div>
        )}
      </main>

      <div data-debug-id="Level-1:ChordPalette" className="clickable-area" onClick={e => e.stopPropagation()}>
        <ChordPalette 
          isOpen={!!activeChord && mode === 'edition' && !isReadOnlyMode}
          onBuild={handleChordBuild} 
          onUndo={handleUndo} 
          onClose={() => setActiveChord(null)}
          activeChord={activeChord}
          activeMeasure={activeMeasure}
          onAnnotate={handleAnnotateMeasure}
        />
      </div>

      {/* GIG MODE bottom toolbar */}
      {isGigModeView && (
        <GigToolbar
          transposeAmount={transposeAmount}
          gigChordScale={uiSettings.gigChordScale ?? 1.0}
          onTranspose={handleTranspose}
          onLyricsSizeChange={handleLyricsSizeChange}
          onChordScaleChange={handleGigChordScaleChange}
        />
      )}
      
      <ChordInspector 
        data={inspectorData} 
        mode={uiSettings.inspectorMode}
        isPinned={isInspectorPinned}
        onTogglePin={() => {
          setIsInspectorPinned(!isInspectorPinned);
          if (!isInspectorPinned) {
            cancelAutoClose();
          }
        }}
        onClose={() => {
          setInspectorData(null);
          setIsInspectorPinned(false);
          cancelAutoClose();
        }}
        onMouseEnter={() => {
          cancelCloseTimeout();
          setIsInspectorHovered(true);
        }}
        onMouseLeave={() => {
          setIsInspectorHovered(false);
          const isMobileOrTablet = window.innerWidth <= 1024 || 'ontouchstart' in window;
          if (!isMobileOrTablet && !isInspectorPinned) {
            setInspectorData(null);
          }
        }}
      />

      {/* Loading Overlay for chart loading */}
      <LoadingOverlay 
        isVisible={isLoadingChart} 
        message={loadingChartMessage} 
      />

      {modals.isLoadModalOpen && (
        <LoadChartModal 
          chartTitles={savedCharts}
          chartsWithRecording={new Set(savedChartsData.filter(c => (c.recordedNotes?.length ?? 0) > 0).map(c => c.songTitle))}
          onLoad={async (title) => {
            if (showWelcome) completeWelcome();
            await handleLoadChart(title);
          }}
          onDelete={handleDeleteChart}
          onClose={() => modals.closeLoadModal()}
          onRefresh={userProfile ? handleRefreshCharts : undefined}
          isRefreshing={modals.isLoadModalRefreshing}
          onImportJSON={handleImportJSON}
          onExportJSON={handleExportJSON}
        />
      )}

      <InsertMeasuresDialog
        isOpen={modals.isInsertMeasuresDialogOpen}
        measureIndex={modals.insertMeasuresDialogIndex}
        onClose={handleInsertMeasuresCancel}
        onInsert={handleInsertMeasuresConfirm}
      />

      <DeleteMeasureDialog
        isOpen={modals.isDeleteMeasureDialogOpen}
        measureIndex={modals.deleteMeasureDialogIndex}
        onClose={handleDeleteMeasureCancel}
        onDelete={handleDeleteMeasureConfirm}
      />

      {modals.showSetListManager && (
        <CollectionManager
          collections={setLists}
          savedChartsData={savedChartsData}
          onCreateCollection={handleCreateSetList}
          onUpdateCollection={handleUpdateSetList}
          onDeleteCollection={handleDeleteSetList}
          onAddChartToCollection={handleAddChartToSetList}
          onRemoveChartFromCollection={handleRemoveChartFromSetList}
          onClose={() => modals.closeSetListManager()}
        />
      )}

      {/* Gig Mode Set List Selector */}
      {modals.showGigSetListSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-debug-id="Level-1:SetListModal">
          <div className="rounded-lg p-6 w-96 max-w-[90vw] max-h-[80vh] overflow-y-auto" data-debug-id="Level-2:SetListModalContent" style={{ backgroundColor: '#161616' }}>
            <div className="flex justify-between items-center mb-4" data-debug-id="Level-2:SetListModalHeader">
              <h2 className="text-xl font-bold text-white">Select Set List</h2>
              <button
                onClick={() => modals.closeGigSetListSelector()}
                className="text-gray-400 hover:text-white transition-colors text-xl font-bold"
                data-debug-id="Level-2:SetListModalCloseButton"
              >
                ✕
              </button>
            </div>
            
            {setLists.length === 0 ? (
              <div className="text-center py-8" data-debug-id="Level-2:NoSetListsState">
                <p className="text-gray-400 mb-4">No set lists available</p>
                <button
                  onClick={() => {
                    modals.closeGigSetListSelector();
                    modals.openSetListManager();
                  }}
                  className="px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded font-semibold transition-colors"
                  data-debug-id="Level-2:CreateFirstSetListButton"
                >
                  Create Set List
                </button>
              </div>
            ) : (
              <div className="space-y-2" data-debug-id="Level-2:SetListOptionsGrid">
                {setLists.map((setList) => (
                  <div
                    key={setList.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      gigSetListId === setList.id
                        ? 'bg-orange-600 border-orange-500 text-white'
                        : 'bg-gray-700 border-gray-600 hover:bg-gray-600 text-gray-100'
                    }`}
                    onClick={() => selectGigSetList(setList.id)}
                    data-debug-id="Level-2:SetListOption"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold truncate">{setList.name}</h3>
                        {setList.description && (
                          <p className="text-sm text-gray-300 truncate">{setList.description}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">
                          {setList.chartTitles.length} songs
                        </p>
                      </div>
                      {setList.color && (
                        <div
                          className="w-4 h-4 rounded-full ml-2 flex-shrink-0"
                          style={{ backgroundColor: setList.color }}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="mt-6 flex justify-between" data-debug-id="Level-2:SetListModalActions">
              <button
                onClick={() => {
                  modals.closeGigSetListSelector();
                  modals.openSetListManager();
                }}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded font-semibold transition-colors"
                data-debug-id="Level-2:ManageSetListsButton"
              >
                Manage Set Lists
              </button>
              {gigSetListId && (
                <button
                  onClick={() => {
                    clearGigSetList();
                    modals.closeGigSetListSelector();
                  }}
                  className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded font-semibold transition-colors"
                  data-debug-id="Level-2:ClearCurrentSetListButton"
                >
                  Clear Current
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      
      {modals.isSaveAsModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="rounded-lg p-6 w-96 max-w-[90vw]" style={{ backgroundColor: '#161616' }}>
            <h2 className="text-xl font-bold text-white mb-4">Save Chart As</h2>
            <input
              type="text"
              value={modals.saveAsTitle}
              onChange={(e) => modals.setSaveAsTitle(e.target.value)}
              placeholder="Enter chart title"
              className="w-full p-3 rounded-md bg-gray-700 text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-cyan-500 mb-4"
              autoFocus
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSaveAs(modals.saveAsTitle);
                }
              }}
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  modals.closeSaveAsModal();
                  modals.setSaveAsTitle('');
                }}
                className="px-4 py-2 rounded-md bg-gray-600 hover:bg-gray-500 text-white font-semibold transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSaveAs(modals.saveAsTitle)}
                disabled={!modals.saveAsTitle.trim()}
                className="px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white font-semibold transition-colors disabled:bg-gray-500 disabled:cursor-not-allowed"
              >
                Save As
              </button>
            </div>
          </div>
        </div>
      )}

      {modals.isNewChartChoiceModalOpen && (
        <NewChartChoiceModal
          onClose={() => modals.closeNewChartChoiceModal()}
          onChooseChordGrid={() => {
            modals.closeNewChartChoiceModal();
            if (newChartChoiceFromWelcome) {
              completeWelcome();
              confirmEmptyChart();
            } else {
              handleEmptyChart();
            }
          }}
          onChoosePianoRec={() => {
            modals.closeNewChartChoiceModal();
            confirmEmptyChart();
            modals.openMidiDaw();
          }}
        />
      )}

      {modals.isEmptyChartModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="rounded-lg p-6 w-96 max-w-[90vw]" style={{ backgroundColor: '#161616' }}>
            <h2 className="text-xl font-bold text-white mb-4">⚠️ New Chart</h2>
            <div className="text-gray-300 mb-6 leading-relaxed">
              <p className="mb-3">Are you sure you want to create a new chart?</p>
              <p className="mb-3">This will clear <strong className="text-white">all chords, lyrics, and annotations</strong> from the current chart.</p>
              <p className="text-yellow-400">💾 Make sure you've saved your work if you want to keep it!</p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => modals.closeEmptyChartModal()}
                className="px-4 py-2 rounded-md bg-gray-600 hover:bg-gray-500 text-white font-semibold transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmEmptyChart}
                className="px-4 py-2 rounded-md bg-orange-600 hover:bg-orange-500 text-white font-semibold transition-colors"
              >
                New Chart
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Name Required Modal */}
      {modals.isNameRequiredModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="rounded-lg p-6 w-96 max-w-[90vw]" style={{ backgroundColor: '#161616' }}>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="text-2xl">🎵</span> Name Your Chart
            </h2>
            <div className="text-gray-300 mb-6 leading-relaxed">
              <p className="mb-3">Please give your chart a title before saving.</p>
              <p className="text-gray-400 text-sm">You can set the title in the header at the top of the screen.</p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => modals.closeNameRequiredModal()}
                className="px-4 py-2 rounded-md bg-cyan-600 hover:bg-cyan-500 text-white font-semibold transition-colors"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Panel */}
      <SettingsPanel 
        isOpen={modals.isRightDockOpen} 
        onClose={() => modals.closeSettings()}
        onPrivacyPolicyClick={() => modals.openPrivacyPolicy()}
        onContactClick={() => modals.openContact()}
        onHelpClick={() => modals.openHelp()}
      >
        {/* User Preferences Section */}
        <div data-debug-id="Level-3:RightDock-PreferencesSection">
          <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">PREFERENCES</h3>
          <div className="bg-gray-700 rounded-lg p-4 space-y-4">
            
            {/* Start App Preference */}
            <div className="flex items-center justify-between" data-debug-id="Level-4:RightDock-StartAppPref">
              <div>
                <label className="text-white font-medium">App Startup</label>
                <p className="text-sm text-gray-400">Choose what to load when app starts</p>
              </div>
              <div className="flex flex-col space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="startAppPreference"
                    checked={userPreferences.startAppOnLastChart}
                    onChange={() => setUserPreferences(prev => ({ ...prev, startAppOnLastChart: true }))}
                    className="mr-2 text-cyan-500"
                  />
                  <span className="text-sm text-gray-300">Last Chart</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="startAppPreference"
                    checked={!userPreferences.startAppOnLastChart}
                    onChange={() => setUserPreferences(prev => ({ ...prev, startAppOnLastChart: false }))}
                    className="mr-2 text-cyan-500"
                  />
                  <span className="text-sm text-gray-300">New Chart</span>
                </label>
              </div>
            </div>

          </div>
        </div>

        {/* UI Controls Section */}
        <div data-debug-id="Level-3:RightDock-UIControlsSection">
          <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">UI CONTROLS</h3>
          <div className="bg-gray-700 rounded-lg p-4 space-y-4" data-debug-id="Level-2:RightDock-ExtraSection">
            
            {/* Grid Layout Mode */}
            <div data-debug-id="Level-3:RightDock-GridLayout">
              <div className="flex items-center justify-between w-full">
                <div>
                  <label className="text-white font-medium">Grid Layout</label>
                  <p className="text-sm text-gray-400">Choose how measures are arranged</p>
                </div>
                <select 
                  value={responsiveMode}
                  onChange={(e) => setResponsiveMode(e.target.value as ResponsiveMode)}
                  className="bg-gray-600 border border-gray-500 rounded px-3 py-1 text-white focus:outline-none focus:border-cyan-400 text-sm"
                >
                  <option value="adaptive">🤖 Adaptive (AI)</option>
                  <option value="current">Current</option>
                  <option value="container">Container</option>
                  <option value="smart">Smart</option>
                  <option value="dynamic">Dynamic</option>
                  <option value="height">Height 🎯</option>
                </select>
              </div>
              {/* Status info */}
              <div className="mt-2 text-xs text-gray-400">
                {numberOfMeasures} measures, {viewportHeight}h
                {responsiveMode === 'height' && measuredLayout && (
                  <>, {measuredLayout.columns}×{measuredLayout.rows} OPTIMIZED</>
                )}
                {responsiveMode === 'height' && !measuredLayout && (
                  <>, MEASURING...</>
                )}
                {responsiveMode === 'adaptive' && (
                  <>, {deviceCategory.icon} {deviceCategory.category.toUpperCase()}</>
                )}
              </div>
            </div>
            
            {/* Inspector Toggle */}
            <div data-debug-id="Level-3:RightDock-InspectorToggle">
              <ToggleSwitch
                id="inspector-toggle"
                label="Inspector"
                enabled={uiSettings.inspectorEnabled}
                onToggle={() => setUiSettings({ ...uiSettings, inspectorEnabled: !uiSettings.inspectorEnabled })}
              />
            </div>
            
            {/* Inspector Mode Selector */}
            {uiSettings.inspectorEnabled && (
              <div className="pl-2" data-debug-id="Level-4:RightDock-InspectorModeSelector">
                <div className="flex items-center justify-between w-full">
                  <label className="font-semibold text-sm text-gray-300">View:</label>
                  <div className="flex items-center rounded-full bg-gray-600 p-1">
                    <button 
                      onClick={() => setUiSettings({...uiSettings, inspectorMode: 'piano'})} 
                      className={`px-3 py-0.5 text-xs rounded-full transition-colors ${uiSettings.inspectorMode === 'piano' ? 'bg-cyan-500 text-white cursor-default' : 'text-gray-400 hover:text-gray-300'}`}
                    >
                      Piano
                    </button>
                    <button 
                      onClick={() => setUiSettings({...uiSettings, inspectorMode: 'guitar'})} 
                      className={`px-3 py-0.5 text-xs rounded-full transition-colors ${uiSettings.inspectorMode === 'guitar' ? 'bg-cyan-500 text-white cursor-default' : 'text-gray-400 hover:text-gray-300'}`}
                    >
                      Guitar
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Lyrics Font Size */}
            <div data-debug-id="Level-3:RightDock-LyricsFontSize">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2">
                  <label className="font-semibold text-sm text-gray-300">Lyrics Size:</label>
                  <div className="flex items-baseline gap-1 text-gray-300" aria-label="Lyrics font size">
                    <span className="font-bold" style={{fontSize: '0.9rem'}}>A</span>
                    <span className="font-bold" style={{fontSize: '1.2rem'}}>A</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => handleLyricsSizeChange(-1)} 
                    className="w-8 h-8 rounded-md bg-gray-600 hover:bg-gray-500 flex items-center justify-center font-bold text-lg transition-colors"
                  >
                    -
                  </button>
                  <button 
                    onClick={() => handleLyricsSizeChange(1)} 
                    className="w-8 h-8 rounded-md bg-gray-600 hover:bg-gray-500 flex items-center justify-center font-bold text-lg transition-colors"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            {/* Gig Chord Size — visible uniquement en Gig mode / Jam session */}
            {isGigModeView && (
              <div data-debug-id="Level-3:RightDock-GigChordScale">
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-2">
                    <label className="font-semibold text-sm text-gray-300">Chord Size:</label>
                    <span className="text-xs text-cyan-400">{Math.round((uiSettings.gigChordScale ?? 1.0) * 100)}%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleGigChordScaleChange(-0.05)}
                      className="w-8 h-8 rounded-md bg-gray-600 hover:bg-gray-500 flex items-center justify-center font-bold text-lg transition-colors"
                    >
                      -
                    </button>
                    <button
                      onClick={() => handleGigChordScaleChange(0.05)}
                      className="w-8 h-8 rounded-md bg-gray-600 hover:bg-gray-500 flex items-center justify-center font-bold text-lg transition-colors"
                    >
                      +
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">Gig &amp; Jam mode · 70%–250%</p>
              </div>
            )}
            
            {/* Debug Overlay Toggle - Development or Secret Debug Mode */}
            {(IS_DEVELOPMENT || secretDebugEnabled) && (
              <div data-debug-id="Level-3:RightDock-DebugToggle">
                <div className="border-t border-gray-600 pt-4">
                  <div className="flex items-center justify-between w-full mb-3">
                    <label className="font-semibold text-sm text-gray-300">Debug Overlay:</label>
                    <button
                      onClick={() => setUiSettings(prev => ({ ...prev, isDebugOverlayVisible: !prev.isDebugOverlayVisible }))}
                      className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${
                        uiSettings.isDebugOverlayVisible 
                          ? 'bg-cyan-600 hover:bg-cyan-500 text-white' 
                          : 'bg-gray-600 hover:bg-gray-500 text-gray-300'
                      }`}
                    >
                      {uiSettings.isDebugOverlayVisible ? 'ON' : 'OFF'}
                    </button>
                  </div>
                  {uiSettings.isDebugOverlayVisible && (
                    <div className="flex items-center justify-between w-full">
                      <span className="text-xs text-gray-400">Level:</span>
                      <div className="flex items-center rounded-full bg-gray-600 p-1">
                        <button 
                          onClick={() => setUiSettings(prev => ({ ...prev, debugOverlayLevel: 1 }))} 
                          className={`px-3 py-0.5 text-xs rounded-full transition-colors ${(uiSettings.debugOverlayLevel || 1) === 1 ? 'bg-cyan-500 text-white' : 'text-gray-300 hover:text-white'}`}
                        >
                          1
                        </button>
                        <button 
                          onClick={() => setUiSettings(prev => ({ ...prev, debugOverlayLevel: 2 }))} 
                          className={`px-3 py-0.5 text-xs rounded-full transition-colors ${uiSettings.debugOverlayLevel === 2 ? 'bg-cyan-500 text-white' : 'text-gray-300 hover:text-white'}`}
                        >
                          2
                        </button>
                        <button 
                          onClick={() => setUiSettings(prev => ({ ...prev, debugOverlayLevel: 3 }))} 
                          className={`px-3 py-0.5 text-xs rounded-full transition-colors ${uiSettings.debugOverlayLevel === 3 ? 'bg-purple-500 text-white' : 'text-gray-300 hover:text-white'}`}
                        >
                          3
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>
        </div>

        {/* MIDI Input Section */}
        <div data-debug-id="Level-3:RightDock-MidiSection">
          <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">MIDI INPUT</h3>
          <MidiPanel
            midiInputs={midiInputs}
            selectedMidiInputId={selectedMidiInputId}
            onSelectMidiInput={setSelectedMidiInputId}
            lastMidiMessage={lastMidiMessage}
          />
        </div>

        {/* Piano Recorder (MiniDaw) Section */}
        <div data-debug-id="Level-3:RightDock-PianoRecorderSection">
          <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">PIANO RECORDER</h3>
          <button
            onClick={() => modals.openMidiDaw()}
            className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-md font-semibold transition-colors"
          >
            Open Piano Recorder
          </button>
        </div>
      </SettingsPanel>

      {/* Account Panel */}
      <AccountPanel
        isOpen={isAccountPanelOpen}
        onClose={() => setIsAccountPanelOpen(false)}
        userProfile={userProfile}
        onRequestDrivePermissions={requestDrivePermissions}
        onDisconnectDrive={disconnectDrive}
        onManageBilling={handleOpenStripePortal}
        onUpgrade={() => modals.openUpgradeModal()}
        onToggleDevMode={() => setDevMode(!subscription.isPremium)}
      />

      {/* Right Dock Toggle Button - Hidden (Settings now accessible via Settings button) */}
      {false && (
        <button
          onClick={() => modals.openSettings()}
          className={`fixed top-1/4 right-0 transform -translate-y-1/2 bg-gray-700 hover:bg-gray-600 text-white p-3 rounded-l-lg shadow-lg transition-all duration-200 z-30 ${
            modals.isRightDockOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'
          }`}
          data-debug-id="Level-1:RightDock-ToggleButton"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}
      
      {/* Jam Session Dialog */}
      {modals.showJamSessionDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={handleCloseJamDialog}>
          <div className="rounded-lg p-6 w-full max-w-md mx-4" onClick={e => e.stopPropagation()} style={{ backgroundColor: '#161616' }}>
            <h2 className="text-xl font-bold text-white mb-4">Join or Create Jam Session</h2>
            
            <div className="space-y-4">
              {/* Create Session Option */}
              <div className="border border-gray-600 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-purple-400 mb-2">Create New Session</h3>
                <p className="text-gray-300 text-sm mb-3">Start a new jam session and share your chart with others.</p>
                <button
                  onClick={handleCreateJamSession}
                  className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-md font-semibold transition-colors"
                >
                  Create & Host Session
                </button>
              </div>

              {/* Join Session Option */}
              <div className="border border-gray-600 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-cyan-400 mb-2">Join Existing Session</h3>
                <p className="text-gray-300 text-sm mb-3">Enter a session code to join someone else's jam session.</p>
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Enter session code (e.g., ABC123)"
                    value={joinSessionCode}
                    onChange={(e) => setJoinSessionCode(e.target.value.toUpperCase())}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    maxLength={6}
                  />
                  <button
                    type="button"
                    onClick={handleJoinJamSession}
                    disabled={!joinSessionCode.trim() || isJoiningSession}
                    className="w-full px-4 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-md font-semibold transition-colors"
                  >
                    {isJoiningSession ? 'Joining...' : 'Join Session'}
                  </button>
                </div>
              </div>
            </div>

            {/* Cancel Button */}
            <button
              onClick={handleCloseJamDialog}
              disabled={isJoiningSession}
              className="w-full mt-4 px-4 py-2 bg-gray-600 hover:bg-gray-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-md font-semibold transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      
      {(IS_DEVELOPMENT || secretDebugEnabled) && uiSettings.isDebugOverlayVisible && <DebugOverlay level={uiSettings.debugOverlayLevel || 1} />}
      
      {/* WebRTC Debugger - Hidden in production unless secret debug enabled */}
      {(IS_DEVELOPMENT || secretDebugEnabled) && (
        <WebRTCDebugger 
          isVisible={modals.showWebRTCDebugger} 
          onToggle={() => modals.toggleWebRTCDebugger()} 
        />
      )}
      
      {/* Fallback debug button - Hidden in production unless secret debug enabled */}
      {(IS_DEVELOPMENT || secretDebugEnabled) && (
        <button
          onClick={() => modals.toggleWebRTCDebugger()}
          className="fixed bottom-4 left-4 bg-green-500 text-white p-3 rounded-full shadow-lg text-sm hover:bg-green-600 transition-colors"
          style={{ zIndex: 10000 }}
          title="Toggle WebRTC Debug (Fallback)"
        >
          📱
        </button>
      )}

      {/* Secret debug indicator */}
      {secretDebugEnabled && !IS_DEVELOPMENT && (
        <div className="fixed top-4 right-4 bg-yellow-600 text-black px-2 py-1 rounded text-xs z-50">
          🔓 Debug Mode
        </div>
      )}

      {/* Feature Limit Modal (Freemium gates) */}
      <FeatureLimitModal
        isOpen={featureLimitModalOpen}
        onClose={() => setFeatureLimitModalOpen(false)}
        onUpgrade={(feature) => {
          setFeatureLimitModalOpen(false);
          upgradeToPremium(feature);
        }}
        messageKey={featureLimitMessageKey}
      />

      {/* Upgrade Modal */}
      <UpgradeModal 
        isOpen={modals.isUpgradeModalOpen}
        onClose={() => {
          modals.closeUpgradeModal();
        }}
        triggerFeature={modals.upgradeTriggerFeature}
      />

      {/* Piano Recorder (MiniDaw) */}
      <MidiDawModal
        isOpen={modals.isMidiDawOpen}
        onClose={() => { modals.closeMidiDaw(); setMidiDawStartInScoreFullScreen(false); }}
        startInScoreFullScreen={midiDawStartInScoreFullScreen}
        songTitle={songTitle}
        onSongTitleChange={setSongTitle}
        songAuthor={songAuthor}
        onSongAuthorChange={setSongAuthor}
        songStyle={songStyle}
        onSongStyleChange={setSongStyle}
        saveStatus={saveStatus}
        hasUnsavedChanges={hasUnsavedChanges}
        bpm={audioSettings.bpm}
        timeSignature={timeSignature}
        recordedNotes={recordedNotes}
        onNotesChange={setRecordedNotes}
        midiInput={midiInputs.find(input => input.id === selectedMidiInputId) || null}
        onSendChordsToChart={(detectedMeasureChords: Map<number, DetectedChord[]>) => {
          updateSourceChart(prevChart => {
            const newChart = prevChart.map(m => ({ ...m, chords: [...m.chords] }));
            detectedMeasureChords.forEach((chords, measureIndex) => {
              if (measureIndex < 0 || measureIndex >= newChart.length || chords.length === 0) return;
              const measure = newChart[measureIndex];
              const alreadyWritten = measure.chords.some(c => !!c && c.trim().length > 0);
              if (alreadyWritten) return; // never overwrite a measure the musician already wrote
              const slotCount = Math.max(1, Math.min(4, chords.length));
              const ordered = [...chords].sort((a, b) => (a.startTick ?? 0) - (b.startTick ?? 0));
              const nextChords = Array(slotCount).fill('');
              ordered.slice(0, slotCount).forEach((c, i) => { nextChords[i] = c.symbol; });
              measure.chords = nextChords;
            });
            return newChart;
          });
        }}
        renderChartPane={() => (
          <div className="w-full h-full overflow-y-auto bg-[#F8F7F4] p-3 pointer-events-none select-none">
            <ChartGrid
              mode="edition"
              chart={displayedChart}
              activeChord={null}
              onChordClick={() => {}}
              onSubdivisionChange={() => {}}
              onPlayChord={() => {}}
              timeSignature={timeSignature}
              currentMeasure={null}
              currentChordIndex={null}
              startMeasureIndex={startMeasureIndex}
              onSetStartMeasure={() => {}}
              invalidChords={invalidChords}
              clipboard={null}
              onCopyMeasure={() => {}}
              onPasteMeasure={() => {}}
              onChordHover={() => {}}
              onChordLeave={() => {}}
              onLyricChange={() => {}}
              onInsertMeasures={() => {}}
              onDeleteMeasure={() => {}}
              lyricsFontSize={uiSettings.lyricsFontSize}
              customGridClasses="grid grid-cols-2 sm:grid-cols-3 gap-2"
              inspectorMode={uiSettings.inspectorMode}
              inspectorEnabled={uiSettings.inspectorEnabled}
              chartHasFine={chartHasFine}
              chartHasToCoda={chartHasToCoda}
            />
          </div>
        )}
      />

      {/* Premium Success Modal */}
      <PremiumSuccessModal
        isOpen={modals.isPremiumSuccessModalOpen}
        onClose={() => modals.closePremiumSuccessModal()}
      />

      {/* Coming Soon Modal */}
      {modals.showComingSoonModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
          onClick={() => modals.closeComingSoon()}
        >
          <div 
            className="rounded-lg shadow-2xl w-full max-w-md p-6"
            style={{ backgroundColor: '#161616' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-cyan-500/20 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <h2 className="text-2xl font-bold text-white text-center mb-2">Coming Soon!</h2>
            <p className="text-gray-300 text-center mb-6">
              The <span className="text-cyan-400 font-semibold">{modals.comingSoonFeature}</span> feature is currently under development and will be available in a future update.
            </p>
            <button
              onClick={() => modals.closeComingSoon()}
              className="w-full bg-cyan-500 hover:bg-cyan-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
            >
              Got it!
            </button>
          </div>
        </div>
      )}

      {/* Contact Modal */}
      <ContactModal
        isOpen={modals.showContactModal}
        onClose={() => modals.closeContact()}
      />

      {/* Help Modal */}
      <HelpModal
        isOpen={modals.showHelpModal}
        onClose={() => modals.closeHelp()}
      />

      {/* Privacy Policy Modal */}
      <PrivacyPolicyModal
        isOpen={modals.showPrivacyPolicyModal}
        onClose={() => modals.closePrivacyPolicy()}
        onContactClick={() => modals.openContact()}
      />

      {/* Terms of Service Modal */}
      <TermsOfServiceModal
        isOpen={modals.showTermsOfServiceModal}
        onClose={() => modals.closeTermsOfService()}
        onContactClick={() => modals.openContact()}
        onPrivacyClick={() => modals.openPrivacyPolicy()}
      />

      {/* Legal Notice Modal (Mentions Légales) */}
      <LegalNoticeModal
        isOpen={modals.showLegalNoticeModal}
        onClose={() => modals.closeLegalNotice()}
        onContactClick={() => modals.openContact()}
      />

      {/* PDF Export Configuration Modal */}
      <PDFExportModal
        isOpen={modals.showPDFExportModal}
        onClose={() => modals.closePDFExportModal()}
        onExport={handlePDFExportWithOptions}
        chart={{
          songTitle,
          songAuthor,
          songStyle,
          numberOfMeasures,
          timeSignature,
          keySignature,
          sourceChart,
          transposeAmount,
          audioSettings,
          uiSettings,
          lastModified: new Date().toISOString(),
        }}
      />

      {/* Printable Chart (Hidden, used for PDF export) */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
        <PrintableChart 
          chart={{
            songTitle,
            songAuthor,
            songStyle,
            numberOfMeasures,
            timeSignature,
            keySignature,
            sourceChart,
            transposeAmount,
            audioSettings,
            uiSettings,
            lastModified: new Date().toISOString(),
          }}
        />
      </div>

      {/* Footer with legal links - visible without login (required by Google OAuth) - Hidden in Gig mode */}
      {!isGigModeView && (
      <footer className="fixed bottom-0 left-0 right-0 py-2 text-center text-xs text-gray-500 bg-gray-900/80 backdrop-blur-sm z-10" style={{ display: modals.showSetListManager ? 'none' : undefined }}>
        <button 
          onClick={() => modals.openPrivacyPolicy()} 
          className="hover:text-gray-300 transition-colors"
        >
          Privacy Policy
        </button>
        <span className="mx-2">|</span>
        <button 
          onClick={() => modals.openTermsOfService()} 
          className="hover:text-gray-300 transition-colors"
        >
          Terms of Service
        </button>
        <span className="mx-2">|</span>
        <button 
          onClick={() => modals.openLegalNotice()} 
          className="hover:text-gray-300 transition-colors"
        >
          Legal Notice
        </button>
        <span className="mx-2">|</span>
        <a
          href="https://blog.chordnotator.com"
          target="_blank"
          rel="noopener"
          className="hover:text-gray-300 transition-colors"
        >
          Blog
        </a>
      </footer>
      )}

      {/* Landing page overlay — above app content, below dialogs and side panels */}
      {showWelcome && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 35 }}>
          {/* Mobile landing page */}
          <div className="block lg:hidden h-full">
            <WelcomeScreen
              onNewChart={() => {
                setNewChartChoiceFromWelcome(true);
                modals.openNewChartChoiceModal();
              }}
              onOpen={async () => {
                setLoadingChartMessage('Loading charts...');
                setIsLoadingChart(true);
                try {
                  await loadSavedChartTitles();
                  modals.openLoadModal();
                } finally {
                  setIsLoadingChart(false);
                }
              }}
              onAccount={() => setIsAccountPanelOpen(true)}
              onSettings={() => modals.openSettings()}
              onJamSession={() => modals.openJamSessionDialog()}
            />
          </div>
          {/* Desktop landing page */}
          <div className="hidden lg:flex h-full">
            <LandingPageDesktop
              onChooseChordGrid={() => {
                completeWelcome();
                confirmEmptyChart();
              }}
              onChoosePianoRec={() => {
                confirmEmptyChart();
                modals.openMidiDaw();
              }}
              onOpen={async () => {
                setLoadingChartMessage('Loading charts...');
                setIsLoadingChart(true);
                try {
                  await loadSavedChartTitles();
                  modals.openLoadModal();
                } finally {
                  setIsLoadingChart(false);
                }
              }}
              onAccount={() => setIsAccountPanelOpen(true)}
              onSettings={() => modals.openSettings()}
              onHelp={() => modals.openHelp()}
              onSetlists={async () => {
                await loadSetLists();
                modals.openSetListManager();
              }}
              onJamSession={() => modals.openJamSessionDialog()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Main App wrapped with AuthProvider and SubscriptionProvider
const AppWithSubscription: React.FC = () => {
  console.log('🏗️ AppWithSubscription component rendering...');
  
  // Check for preview mode via URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  const previewMode = urlParams.get('preview');
  
  console.log('🔍 Preview mode check:', { previewMode, fullURL: window.location.href });
  
  // Show preview page if requested (no auth/subscription needed)
  if (previewMode === 'jam-menu') {
    console.log('✅ Loading JamSessionMenuPreview component...');
    return <JamSessionMenuPreview />;
  }
  
  console.log('⏭️ Loading main App...');

  // Dev tool : Architecture Explorer accessible via localhost/#arch
  if (window.location.hash === '#arch') {
    return <ArchitecturePage />;
  }
  
  return (
    <AuthProvider>
      <SubscriptionProvider>
        <App />
      </SubscriptionProvider>
    </AuthProvider>
  );
};

export default AppWithSubscription;
